import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Alert,
    AppBar,
    Autocomplete,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Drawer,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    Menu,
    MenuItem,
    Paper,
    Pagination,
    Fade,
    Select,
    Skeleton,
    Slider,
    Snackbar,
    SpeedDial,
    SpeedDialAction,
    Switch,
    TextField,
    Tooltip,
    Toolbar,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    InputAdornment,
    CssBaseline,
    useMediaQuery,
} from "@mui/material";

import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    Legend,
    ResponsiveContainer
} from "recharts";

import Grid from "@mui/material/GridLegacy";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import LinearProgress from "@mui/material/LinearProgress";

import AddIcon from "@mui/icons-material/Add";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DoneIcon from "@mui/icons-material/Done";
import DownloadIcon from "@mui/icons-material/Download";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import FolderIcon from "@mui/icons-material/Folder";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import InfoIcon from "@mui/icons-material/Info";
import LanguageIcon from "@mui/icons-material/Language";
import LoopIcon from "@mui/icons-material/Loop";
import MapIcon from "@mui/icons-material/Map";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import MenuIcon from "@mui/icons-material/Menu";
import GroupIcon from "@mui/icons-material/Group";
import PaymentIcon from "@mui/icons-material/Payment";
import ReceiptIcon from "@mui/icons-material/Receipt";
import SearchIcon from "@mui/icons-material/Search";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import LogoutIcon from "@mui/icons-material/Logout";
import RefreshIcon from "@mui/icons-material/Refresh";
import TuneIcon from "@mui/icons-material/Tune";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArticleIcon from "@mui/icons-material/Article";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import LockIcon from '@mui/icons-material/Lock';
import UndoIcon from '@mui/icons-material/Undo';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import CloseIcon from "@mui/icons-material/Close";
/* =========================
   CENTRALIZED CONSTS
========================= */

const UI = {
    BRAND: {
        ALT_LOGO: "pSpace research",
        LOGO_URL: "./pspace_research_logo.png",
        PRODUCT_LOGO_URL: "./atlas_pspace_research_logo.png",
        PRIMARY_COLOR: "rgb(12, 5, 109)",
        LOGIN_SCREEN_BACKGROUND_IMAGE: "https://www.pspace-research.com/files/images/stock_images/spiral.png",
        DARK_LOGIN_SCREEN_BACKGROUND_IMAGE: true
    },
    LAYOUT: {
        HEADER_HEIGHT: 100,
        FOOTER_HEIGHT: 100,
        CONTENT_WIDTH: "90%",
        CONTENT_PADDING_TOP: 3,
        CONTENT_PADDING_BOTTOM: 6,
        TOP_SPACING: "50px",
        PAGE_SPACING: 5,
        CONTENT_SPACING: 7,
        SPEED_DIAL_BOTTOM: 125,
        SPEED_DIAL_RIGHT: 25,
        PAGE_PADDING_X: 3,
        CARD_RADIUS: 2,
        CARD_GAP: 1.25,
        HELP_DRAWER_WIDTH: 440,
    },

    ARIA: {
        ACTIONS: "Aktionen",
        USER_MENU: "Benutzermenü",
        HELP_SEARCH: "Hilfe durchsuchen",
    },

    TABS: {
        TASKS: "Suchvorgänge",
        RESULTS: "Ergebnisse",
        ORDERS: "Bestellungen",
        USERS: "Benutzer",
    },

    PAGE_HEADERS: {
        TASKS_TITLE: "Suchvorgänge",
        TASKS_SUBTITLE: "Status & Fortschritt Ihrer laufenden und geplanten Suchen.",

        RESULTS_TITLE: "Ergebnisse",
        RESULTS_SUBTITLE: "Preview, Export und Freigabe Ihrer Datensätze.",

        ORDERS_TITLE: "Bestellungen",
        ORDERS_SUBTITLE: "Zahlungsstatus & Rechnungen für Ihre Credit-Pakete.",


        USERS_TITLE: "Benutzerverwaltung",
        USERS_SUBTITLE: "Benutzer, Credits, Zahlungsstatus und Stammdaten verwalten.",
    },

    BUTTONS: {
        LOGIN: "Anmelden",
        CLOSE: "Schließen",
        CANCEL: "Abbrechen",
        PREV: "Zurück",
        NEXT: "Weiter",
        FINISH: "Fertigstellen",

        NEW_SEARCH: "Neue Suche",
        DOWNLOAD_USAGE: "Nutzungsübersicht",
        BUY_PACKAGES: "Credits kaufen",
        REFRESH: "Aktualisieren",
        PREVIEW: "Preview",
        EXPORT: "Export",
        DOWNLOAD_START: "Download starten",
        OPEN_PROGRESS: "Fortschritt",
        SAVE: "Speichern",
        EDIT: "Bearbeiten",
        CREATE_USER: "Benutzer anlegen",
    },

    MENUS: {
        CANCEL_SEARCH: "Suche abbrechen",
        PROFILE: "Mein Profil",
        SUPPORT: "Support",
        HELP: "Hilfe",
        IMPRINT: "Impressum",
        LOGOUT: "Logout",
    },

    LABELS: {
        USER_DISABLED: "Deaktiviert",
        EMAIL: "Emailadresse",
        PASSWORD: "Passwort",
        CREDITS: "Credits",

        MONTHS: "Zeitraum (Monate)",
        MONTH: "Monat",
        DOWNLOADS: "Downloads",

        EXPORT_COUNT: "Anzahl",
        EXPORT_MAX: (n) => `Maximum: ${n}`,
        EXPORT_REQUIRED_CREDITS: "Benötigte Credits",
        EXPORT_NOT_ENOUGH_CREDITS: "Ihr Credit-Guthaben reicht nicht aus.",
        EXPORT_AVAILABLE: (n) => `Verfügbar: ${n} Datensätze`,

        HELP_SEARCH: "Suche in der Hilfe",
        HELP_RESULTS: (n) => `${n} Treffer`,
        HELP_EMPTY_SEARCH: "Keine Treffer. Versuchen Sie einen anderen Suchbegriff.",
        HELP_TIPS_TITLE: "Tipps",
        HELP_TIPS_1: "Suchen Sie nach Begriffen wie „Export“, „Credits“, „Status“ oder „Rechnung“.",
        HELP_TIPS_2: "Die Artikel sind nach Bereichen gruppiert: Suchvorgänge, Ergebnisse und Bestellungen.",

        NO_PAYMENT: "Keine Zahlung",
        SMALL_BUSINESS: "Kleinunternehmer",
        USER_EMAIL: "E-Mail-Adresse (Login)",
        BUSINESS_NAME: "Firmenname",
        NEW_PASSWORD: "Neues Passwort",
        TAX_ID: "Steuer-ID",
        VAT_ID: "Umsatzsteuer-ID"
    },

    DIALOGS: {
        DOWNLOAD_USAGE_TITLE: "Downloads pro Monat",
        IMPRINT_TITLE: "Impressum",
        EXPORT_TITLE: "Export konfigurieren",
        PROFILE_TITLE: "Benutzerprofil",
        PROGRESS_TITLE_PREFIX: "Fortschritt – ",
        PACKAGES_TITLE: "Credits kaufen",
        SUPPORT_TITLE: "Support kontaktieren",
        USER_EDIT_TITLE: "Benutzer bearbeiten",
        CREATE_USER_TITLE: "Benutzer anlegen",
    },

    HELP: {
        TITLE: "Hilfe",
        SUBTITLE: "Kurze Anleitungen & Antworten zu den wichtigsten Funktionen.",
        ARTICLE_LABEL: "Artikel",
        SECTION_LABEL: "Bereiche",
        CONTACT_TITLE: "Kontakt",
        COPY_OK: "Kopiert",
        COPY_FAIL: "Konnte nicht kopieren",
        CLOSE: "Schließen",
    },

    EMPTY: {
        TASKS_TITLE: "Keine Suchvorgänge",
        TASKS_SUBTITLE: "Starten Sie eine neue Audience, um eine Suche zu beginnen.",

        RESULTS_TITLE: "Keine Ergebnisse",
        RESULTS_SUBTITLE: "Sobald eine Suche abgeschlossen ist, erscheinen hier Ihre Datensätze.",

        ORDERS_TITLE: "Keine Bestellungen",
        ORDERS_SUBTITLE: "Kaufen Sie Credits, um Exporte freizuschalten und Rechnungen zu erhalten.",

    },

    TOOLTIPS: {
        TASK_PROGRESS: "Fortschritt anzeigen",
        TASK_MENU: "Weitere Optionen",
        RESULT_PREVIEW: "Preview herunterladen",
        RESULT_EXPORT: "Export herunterladen",
        ORDER_INVOICE: "Rechnung herunterladen",
        AGE_FILTER_INFO:
            "Aktivieren Sie dies, um festzulegen, dass mindestens ein Geschäftsführer in der gewählten Altersspanne liegen muss.",
    },

    FOOTER: {
        TEXT: "ATLAS by pSpace research",
    },

    IMPRINT: {
        COMPANY: "pSpace Research UG (haftungsbeschränkt)",
        ADDRESS_LINE_1: "Mozartstraße 6",
        ADDRESS_LINE_2: "72661 Grafenberg",
        ADDRESS_LINE_3: "Deutschland",
        REPRESENTED_BY_TITLE: "Vertretung der Gesellschaft:",
        REPRESENTED_BY_VALUE: "Geschäftsführer: Philipp Kunz",
        CONTACT_TITLE: "Kontakt:",
        CONTACT_EMAIL: "E-Mail: kontakt@pspace-research.com",
        CONTACT_PHONE: "Telefon: +49 7123 3762207",
        REGISTER_TITLE: "Eintragung im Handelsregister:",
        REGISTER_COURT: "Registergericht: Amtsgericht Stuttgart",
        REGISTER_NUMBER: "Registernummer: HRB 804381",
        VAT_TITLE: "Umsatzsteuer-ID",
        VAT_VALUE: "folgt",
    },

    SUPPORT: {
        EMAIL: "support@pspace-research.com",
        PHONE: "+49 7123 3762207",
        INTRO: "Wenn Sie Hilfe brauchen, erreichen Sie uns:",
        SUBJECT: "Supportanfrage",
        LABEL_EMAIL: "E-Mail",
        LABEL_PHONE: "Telefon",
    },

    MESSAGES: {
        LOGIN_SUCCESS: "Willkommen",
        LOGIN_MISSING_FIELDS: "Bitte Emailadresse und Passwort eingeben",
        LOGIN_INVALID: "Ungültige Zugangsdaten",
        LOGIN_TECHNICAL_ERROR: "Es besteht ein technisches Problem, das Ihren Login verhindert",

        LOAD_TASKS_ERROR: "Fehler beim Laden der Suchvorgänge",
        LOAD_RESULTS_ERROR: "Fehler beim Laden der Ergebnisse",
        LOAD_ORDERS_ERROR: "Fehler beim Laden der Orders",

        CANCEL_TASK_OK: "Der Abbruch der Suche wurde eingeleitet. Bitte warten.",
        CANCEL_TASK_ERROR: "Fehler beim Abbrechen",

        PREVIEW_ERROR: "Fehler beim Preview",
        EXPORT_OK: "Export gestartet",
        EXPORT_ERROR: "Export fehlgeschlagen",

        INVOICE_ERROR: "Rechnung konnte nicht geladen werden",

        PACKAGES_LOAD_ERROR: "Pakete konnten nicht geladen werden",
        PAYMENT_REDIRECT_ERROR: "Weiterleitung zum Zahlungsanbieter fehlgeschlagen",

        PROFILE_LOAD_ERROR: "Profil konnte nicht geladen werden",
        CREDIT_LOAD_ERROR: "Verfügbare Credits konnte nicht geladen werden",
        COSTS_CALC_ERROR: "Kosten konnten nicht berechnet werden",

        DESCRIPTOR_LOAD_ERROR: "Fehler beim Laden der Suchkriterien",
        AUDIENCE_CREATE_OK: "Audience wurde erfolgreich erstellt",
        AUDIENCE_CREATE_ERROR: "Fehler beim Erstellen der Audience",

        HELP_COPY_OK: "Kopiert",
        HELP_COPY_FAIL: "Konnte nicht kopieren",
        ADMIN_USERS_LOAD_ERROR: "Benutzer konnten nicht geladen werden",
        ADMIN_UPDATE_FAILED: "Änderung fehlgeschlagen",
        ADMIN_UPDATE_OK: "Änderungen gespeichert",
        USER_CREATE_OK: "Benutzer wurde angelegt",
        USER_CREATE_ERROR: "Benutzer konnte nicht angelegt werden",
        USER_CREATE_MISSING_FIELDS: "Bitte alle Felder ausfüllen",
        PASSWORD_SET_OK: "Passwort wurde geändert",
        PASSWORD_SET_ERROR: "Passwort konnte nicht geändert werden",

        NO_DATA_AVAILABLE: "Keine Daten für den gewählten Zeitraum verfügbar.",
        USAGE_DATA_LOADING_FAILED: "Usage konnte nicht geladen werden"
    },

    STATUS: {
        TASK: {
            SCHEDULED: "SCHEDULED",
            RUNNING: "RUNNING",
            COMPLETED: "COMPLETED",
            FAILED: "FAILED",
            ABORTED: "ABORTED",
        },
        LABELS: {
            SCHEDULED: "Geplant",
            RUNNING: "Läuft",
            COMPLETED: "Fertig",
            FAILED: "Fehlgeschlagen",
            ABORTED: "Abgebrochen",
            UNKNOWN: "Unbekannt",
        },
    },
    ORDER_STATUS: {
        OPEN: "OPEN",
        PAYMENT_PENDING: "PAYMENT_PENDING",
        PAID: "PAID",
        FAILED: "FAILED",
        EXPIRED: "EXPIRED",
        REFUNDED: "REFUNDED",
        DISPUTED: "DISPUTED",
        DISPUTED_AND_REFUNDED: "DISPUTED_AND_REFUNDED"
    },

    SECTIONS: {
        SECURITY: "Sicherheit",
    },
    COUNTRY: {
        GERMANY: "Deutschland"
    },

    MONTH_OPTIONS: [1, 3, 6, 12],
};

/* =========================
   HELP DOCS (ALL LITERALS CENTRALIZED HERE)
========================= */

export const HELP_DOCS = {
    sections: [
        {
            id: "getting-started",
            title: "Erste Schritte",
            description: "Orientierung, Navigation und neue Audiences",
            articles: [
                {
                    id: "what-is-atlas",
                    title: "Was ist ATLAS?",
                    tags: ["Grundlagen", "Überblick"],
                    body: [
                        "ATLAS unterstützt Sie bei der gezielten Recherche von Unternehmen und beim Aufbau strukturierter Audiences.",
                        "Eine Audience durchläuft mehrere Research-Phasen und wird anschließend als Ergebnis für Vorschau und Export bereitgestellt.",
                        "Exporte werden über Credits abgerechnet. Vorschauen können Sie nutzen, bevor Sie Credits für einen Export einsetzen."
                    ]
                },
                {
                    id: "navigation",
                    title: "Wie navigiere ich durch ATLAS?",
                    tags: ["Navigation", "Speed Dial", "Menü"],
                    body: [
                        "Die Hauptbereiche öffnen Sie über den Speed Dial unten rechts.",
                        "Dort finden Sie Suchvorgänge, Ergebnisse, Bestellungen und – mit entsprechender Berechtigung – die Benutzerverwaltung.",
                        "Profil, Hilfe, Support und Impressum erreichen Sie über das Drei-Punkte-Menü oben rechts."
                    ]
                },
                {
                    id: "start-search",
                    title: "Wie starte ich eine neue Audience?",
                    tags: ["Audience", "Suche", "Start"],
                    body: [
                        "Öffnen Sie über den Speed Dial den Bereich für Suchvorgänge und wählen Sie „Neue Suche“.",
                        "Vergeben Sie einen Audience-Namen und definieren Sie die gewünschten Suchkriterien.",
                        "Legen Sie den geografischen Suchraum fest und schließen Sie die Konfiguration ab.",
                        "ATLAS übernimmt die Audience anschließend in den Research-Prozess."
                    ]
                }
            ]
        },
        {
            id: "research",
            title: "Research-Prozess",
            description: "Fortschritt, Phasen und parallele Vorgänge",
            articles: [
                {
                    id: "research-phases",
                    title: "Welche Research-Phasen gibt es?",
                    tags: ["Research", "Fortschritt", "Phasen"],
                    body: [
                        "Eine Suche durchläuft die Phasen Recherche vorbereiten, Unternehmen identifizieren, Online-Präsenz prüfen, Offizielle Register abgleichen und Ergebnisse aufbereiten.",
                        "Der Gesamtfortschritt fasst den Fortschritt aller Phasen zusammen.",
                        "Wenn Sie mit der Maus über eine Phase fahren, sehen Sie deren individuellen Status und Fortschritt."
                    ]
                },
                {
                    id: "research-rotation",
                    title: "Was passiert bei mehreren aktiven Suchvorgängen?",
                    tags: ["Research", "Rotation", "Vorgänge"],
                    body: [
                        "Sind mehrere Vorgänge aktiv, wechselt die Research-Ansicht dezent zwischen ihnen.",
                        "Die schmale Statusleiste unterhalb des Research-Bereichs zeigt, welcher Vorgang aktuell dargestellt wird.",
                        "Sie können die Rotation pausieren oder über „Übersicht“ alle aktiven Vorgänge öffnen und gezielt zu einem Vorgang springen."
                    ]
                },
                {
                    id: "research-status",
                    title: "Was bedeuten die Status?",
                    tags: ["Status", "Research"],
                    body: [
                        "Geplant: Der Vorgang wartet auf die Verarbeitung.",
                        "Läuft: ATLAS verarbeitet den Vorgang aktuell.",
                        "Fertig: Der Research-Prozess wurde abgeschlossen und ein Ergebnis kann bereitstehen.",
                        "Fehlgeschlagen oder abgebrochen: Der Vorgang wurde nicht regulär abgeschlossen."
                    ]
                },
                {
                    id: "cancel-task",
                    title: "Kann ich einen Vorgang abbrechen?",
                    tags: ["Abbrechen", "Research"],
                    body: [
                        "Geplante oder laufende Vorgänge können über ihre objektbezogenen Aktionen abgebrochen werden.",
                        "Bereits ausgeführte Verarbeitungsschritte werden dadurch nicht rückgängig gemacht."
                    ]
                }
            ]
        },
        {
            id: "results",
            title: "Ergebnisse",
            description: "Audiences finden, prüfen und exportieren",
            articles: [
                {
                    id: "where-results",
                    title: "Wo finde ich abgeschlossene Audiences?",
                    tags: ["Ergebnisse", "Audience"],
                    body: [
                        "Abgeschlossene Research-Vorgänge erscheinen im Bereich „Ergebnisse“.",
                        "Die neuesten Ergebnisse werden zuerst angezeigt.",
                        "Über die Suche im Tabellenkopf können Sie Audiences gezielt finden; die Liste ist seitenweise organisiert."
                    ]
                },
                {
                    id: "preview",
                    title: "Wofür ist die Vorschau gedacht?",
                    tags: ["Vorschau", "CSV"],
                    body: [
                        "Mit der Vorschau prüfen Sie Aufbau und Beispiel-Datensätze einer Audience, bevor Sie einen kostenpflichtigen Export auslösen.",
                        "Die Vorschau selbst verbraucht keine Credits."
                    ]
                },
                {
                    id: "result-size",
                    title: "Was bedeutet die Datensatzanzahl?",
                    tags: ["Datensätze", "Ergebnis"],
                    body: [
                        "Die Datensatzanzahl zeigt, wie viele Unternehmenseinträge aktuell in der Audience enthalten sind.",
                        "Beim Export können Sie abhängig vom verfügbaren Umfang auch Teilmengen auswählen."
                    ]
                }
            ]
        },
        {
            id: "export",
            title: "Export & Credits",
            description: "Vorschau, Export und Credit-Verbrauch",
            articles: [
                {
                    id: "how-export",
                    title: "Wie exportiere ich eine Audience?",
                    tags: ["Export", "Download", "Credits"],
                    body: [
                        "Wählen Sie in der Ergebniszeile „Export“.",
                        "Im Exportdialog legen Sie die gewünschte Anzahl an Datensätzen fest.",
                        "ATLAS zeigt den benötigten Credit-Bedarf vor der Bestätigung an.",
                        "Nach der Bestätigung startet der CSV-Download."
                    ]
                },
                {
                    id: "not-enough-credits",
                    title: "Was passiert bei zu wenig Credits?",
                    tags: ["Credits", "Guthaben"],
                    body: [
                        "Reicht Ihr Guthaben nicht für den gewünschten Export, können Sie weitere Credits erwerben.",
                        "Ihr aktuelles Credit-Guthaben sehen Sie auch in Ihrem Profil."
                    ]
                },
                {
                    id: "multiple-exports",
                    title: "Kann ich eine Audience mehrfach exportieren?",
                    tags: ["Export", "Mehrfach"],
                    body: [
                        "Ja. Ergebnisse können mehrfach und auch in Teilmengen exportiert werden.",
                        "Der jeweilige Credit-Bedarf wird vor jedem Export neu ausgewiesen."
                    ]
                }
            ]
        },
        {
            id: "orders",
            title: "Bestellungen & Rechnungen",
            description: "Credit-Käufe, Zahlungen und Belege",
            articles: [
                {
                    id: "buy-credits",
                    title: "Wie kaufe ich Credits?",
                    tags: ["Credits", "Bestellung"],
                    body: [
                        "„Credits kaufen“ erreichen Sie über den Speed Dial oder bei Bedarf direkt aus dem Exportprozess.",
                        "Wählen Sie ein Paket und schließen Sie die Zahlung beim Zahlungsanbieter ab.",
                        "Nach erfolgreicher Zahlung wird das Guthaben automatisch aktualisiert."
                    ]
                },
                {
                    id: "orders-list",
                    title: "Wie finde ich eine bestimmte Bestellung?",
                    tags: ["Bestellungen", "Suche", "Pagination"],
                    body: [
                        "Bestellungen werden seitenweise angezeigt.",
                        "Über das Suchfeld im Tabellenkopf können Sie nach Bestellnummer, Status, Credits oder Datum suchen."
                    ]
                },
                {
                    id: "invoices",
                    title: "Wo finde ich meine Rechnungen?",
                    tags: ["Rechnung", "PDF"],
                    body: [
                        "Eine verfügbare Rechnung können Sie direkt in der jeweiligen Bestellungszeile öffnen beziehungsweise herunterladen.",
                        "Die Verfügbarkeit richtet sich nach dem Zahlungsstatus der Bestellung."
                    ]
                }
            ]
        },
        {
            id: "profile",
            title: "Profil & Konto",
            description: "Unternehmensdaten, Zugriff und Guthaben",
            articles: [
                {
                    id: "profile-open",
                    title: "Wo finde ich mein Profil?",
                    tags: ["Profil", "Konto", "Menü"],
                    body: [
                        "Öffnen Sie das Drei-Punkte-Menü oben rechts und wählen Sie „Mein Profil“.",
                        "Dort sehen Sie Ihre Unternehmens- und Kontaktdaten, Abrechnungsinformationen, Rolle und das aktuelle Credit-Guthaben."
                    ]
                },
                {
                    id: "password",
                    title: "Wie kann ich mein Passwort ändern?",
                    tags: ["Passwort", "Sicherheit"],
                    body: [
                        "Das Passwort kann derzeit nur durch einen Administrator geändert werden.",
                        "Wenden Sie sich bei Bedarf an Ihren Administrator oder an den Support."
                    ]
                }
            ]
        },
        {
            id: "admin",
            title: "Administration",
            description: "Benutzer und Datenumfang verwalten",
            articles: [
                {
                    id: "admin-users",
                    title: "Wie verwalte ich Benutzer?",
                    tags: ["Admin", "Benutzer"],
                    body: [
                        "Administratoren erreichen die Benutzerverwaltung über den Speed Dial.",
                        "Die Benutzerliste ist paginiert und kann über das Suchfeld im Tabellenkopf gefiltert werden.",
                        "Benutzerspezifische Änderungen öffnen Sie über die Aktion in der jeweiligen Zeile."
                    ]
                },
                {
                    id: "admin-scope",
                    title: "Wie ändere ich den sichtbaren Datenumfang?",
                    tags: ["Admin", "Datenansicht"],
                    body: [
                        "Die Admin-Datenansicht wird über die entsprechende Konfiguration im Speed Dial gesteuert.",
                        "So kann zwischen den eigenen Daten und dem erweiterten administrativen Datenumfang gewechselt werden."
                    ]
                }
            ]
        },
        {
            id: "support",
            title: "Support",
            description: "Hilfe und direkter Kontakt",
            articles: [
                {
                    id: "contact-support",
                    title: "Wie erreiche ich den Support?",
                    tags: ["Support", "Hilfe"],
                    body: [
                        "Nutzen Sie zuerst die Suche in diesem Hilfebereich, um Antworten zu den wichtigsten Funktionen zu finden.",
                        "Den direkten Support erreichen Sie über das Drei-Punkte-Menü oben rechts oder über den Kontaktbereich dieser Hilfe."
                    ]
                }
            ]
        }
    ]
};

/* =========================
   BACKEND CONFIG
========================= */


class BackendPool {
    constructor(backends) {
        this.backends = backends;
        this.index = 0;
        this.acceptingIndex = 0;
    }

    next() {
        const backend = this.backends[this.index % this.backends.length];
        this.index++;
        return backend;
    }

    nextAccepting() {
        const backend = this.backends[this.acceptingIndex % this.backends.length];
        this.acceptingIndex++;
        return backend;
    }


    async isAvailable(backend) {
        try {
            const res = await fetch(`${backend.url}/health`);
            if (!res.ok) return false;
            const data = await res.json();
            return data.acceptingTasks !== false;
        } catch {
            return false;
        }
    }

    async isAccepting(backend) {
        try {
            const res = await fetch(`${backend.url}/health/accepts`);
            if (!res.ok) return false;
            const data = await res.json();
            return data.acceptingTasks !== false;
        } catch {
            return false;
        }
    }

    async pickBackend() {
        const tries = this.backends.length + 2;
        for (let i = 0; i < tries; i++) {
            const backend = this.next();
            const accepting = await this.isAvailable(backend);
            if (accepting) return backend;
        }
        throw new Error("NO_BACKEND_AVAILABLE");
    }

    async pickAcceptingBackend() {
        const tries = this.backends.length;
        for (let i = 0; i < tries; i++) {
            const backend = this.nextAccepting();
            const accepting = await this.isAccepting(backend);
            if (accepting) return backend;
        }
        throw new Error("NO_BACKEND_AVAILABLE");
    }

    async allAvailable() {
        const availble = [];
        const tries = this.backends.length;
        for (let i = 0; i < tries; i++) {
            const backend = this.backends[i];
            if (this.isAvailable(backend))
                availble.push(backend);
        }
        return availble;
    }


    getById(id) {
        return this.backends.find((b) => b.id === id);
    }
}

const backendPool = new BackendPool([
    { id: "backend-0", url: "https://atlas-backend-0.pspace-research.com:443" },
    { id: "backend-1", url: "https://atlas-backend-1.pspace-research.com:443" },
    //{ id: "backend-2", url: "http://192.168.178.76:8100" }
]);



/* =========================
   API ENDPOINTS
========================= */

const endpoints = {
    tasksOwn: baseUrl => `${baseUrl}/scraping/tasks`,
    tasksAll: baseUrl => `${baseUrl}/scraping/tasks/all`,
    abortTask: (baseUrl, id) => `${baseUrl}/scraping/tasks/${id}`,
    login: baseUrl => `${baseUrl}/authentication/login`,
    profile: baseUrl => `${baseUrl}/authentication/profile`,

    sourceDescriptor: (baseUrl, sourceName) => `${baseUrl}/scraping/source/${encodeURIComponent(sourceName)}/descriptor`,
    createRadialAudience: (baseUrl, sourceName) =>
        `${baseUrl}/scraping/source/${encodeURIComponent(sourceName)}/radial_audience`,
    createRangeAudience: (baseUrl, sourceName) =>
        `${baseUrl}/scraping/source/${encodeURIComponent(sourceName)}/range_audience`,

    resultsOwn: baseUrl => `${baseUrl}/result/results`,
    resultsAll: baseUrl => `${baseUrl}/result/results/all`,
    resultPreview: (baseUrl, id) => `${baseUrl}/result/results/${id}/preview`,
    resultExport: (baseUrl, id) => `${baseUrl}/result/results/${id}/export`,
    resultExportCosts: (baseUrl, id) => `${baseUrl}/result/results/${id}/export/costs`,

    orderPackages: baseUrl => `${baseUrl}/order/packages`,
    orderPlace: baseUrl => `${baseUrl}/order/place`,
    ordersOwn: baseUrl => `${baseUrl}/order/orders`,
    ordersAll: baseUrl => `${baseUrl}/order/orders/all`,
    orderInvoice: (baseUrl, id) => `${baseUrl}/order/${id}/invoice`,
    usage: baseUrl => `${baseUrl}/stats/usage`,
};

const adminEndpoints = {
    usersAll: (baseUrl) => `${baseUrl}/admin/user/all`,
    setNoPayment: (baseUrl, id) => `${baseUrl}/admin/user/${id}/flags/no-payment`,
    setSmallBusiness: (baseUrl, id) => `${baseUrl}/admin/user/${id}/flags/small-business`,
    setCredits: (baseUrl, id) => `${baseUrl}/admin/user/${id}/credits/balance`,
    setAddress: (baseUrl, id) => `${baseUrl}/admin/user/${id}/address`,
    setBanking: (baseUrl, id) => `${baseUrl}/admin/user/${id}/banking`,
    setTax: (baseUrl, id) => `${baseUrl}/admin/user/${id}/tax`,
    setBusinessName: (baseUrl, id) => `${baseUrl}/admin/user/${id}/business-name`,
    setPassword: (baseUrl, id) => `${baseUrl}/admin/user/${id}/password`,
    setDisabled: (baseUrl, id) => `${baseUrl}/admin/user/${id}/disabled`,
};

/* =========================
   UTILS
========================= */

const nullToEmpty = (v) => (v == null ? "" : v);


function mapUsageToChartData(usageList) {
    return usageList.map(item => ({
        label: `${item.month}.${item.year}`,
        credits: item.credits,
        downloads: item.count
    }));
}

const downloadTextAsFile = (text, filename, mime = "text/plain;charset=utf-8;") => {
    const blob = new Blob([text], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
};

const downloadBlobAsFile = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
};

const withAuthParams = (username, password, extra = {}) =>
    new URLSearchParams({
        emailAddress: username,
        password,
        ...extra,
    });

const formatDate = (timestamp) => {
    if (!timestamp) return "-";
    return new Date(timestamp).toLocaleDateString("de-DE", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    });
};


function useInterval(callback, delay, enabled = true) {
    const savedCallback = useRef(callback);

    useEffect(() => {
        savedCallback.current = callback;
    }, [callback]);

    useEffect(() => {
        if (!enabled || delay == null) return;
        const id = setInterval(() => savedCallback.current(), delay);
        return () => clearInterval(id);
    }, [delay, enabled]);
}

function useSnackbar() {
    const [state, setState] = useState({ open: false, message: "", severity: "info" });
    const showSnackbar = useCallback((message, severity = "info") => {
        setState({ open: true, message, severity });
    }, []);
    const closeSnackbar = useCallback(() => {
        setState((prev) => ({ ...prev, open: false }));
    }, []);
    return { snackbarState: state, showSnackbar, closeSnackbar };
}

async function copyToClipboard(text) {
    if (!text) return false;
    try {
        if (navigator?.clipboard?.writeText) {
            await navigator.clipboard.writeText(text);
            return true;
        }
    } catch {
        // fallback below
    }
    try {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        return true;
    } catch {
        return false;
    }
}

const formatEuroFromCents = (cents) =>
    typeof cents === "number"
        ? new Intl.NumberFormat("de-DE", {
            style: "currency",
            currency: "EUR",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(cents / 100)
        : "–";

const formatCentsPerCredit = (totalCents, credits) =>
    typeof totalCents === "number" && credits > 0
        ? `${new Intl.NumberFormat("de-DE", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 6,
        }).format(totalCents / credits)} Cent / Credit`
        : "–";


/* =========================
   STATUS UI
========================= */

const getTaskStatusChipMeta = (status) => {
    switch (status) {
        case UI.STATUS.TASK.RUNNING:
            return { label: UI.STATUS.LABELS.RUNNING, color: "info" };
        case UI.STATUS.TASK.SCHEDULED:
            return { label: UI.STATUS.LABELS.SCHEDULED, color: "default" };
        case UI.STATUS.TASK.COMPLETED:
            return { label: UI.STATUS.LABELS.COMPLETED, color: "success" };
        case UI.STATUS.TASK.FAILED:
            return { label: UI.STATUS.LABELS.FAILED, color: "error" };
        default:
            return { label: UI.STATUS.LABELS.UNKNOWN, color: "default" };
    }
};

const getOrderStatusMeta = (status) => {
    switch (status) {

        case UI.ORDER_STATUS.OPEN:
            return {
                icon: <HourglassEmptyIcon />,
                text: "Order wurde erstellt, Zahlung noch nicht gestartet."
            };

        case UI.ORDER_STATUS.PAYMENT_PENDING:
            return {
                icon: <PaymentIcon />,
                text: "Checkout abgeschlossen, Zahlung wird verarbeitet."
            };

        case UI.ORDER_STATUS.PAID:
            return {
                icon: <CheckCircleIcon />,
                text: "Zahlung erfolgreich abgeschlossen."
            };

        case UI.ORDER_STATUS.FAILED:
            return {
                icon: <ErrorOutlineIcon />,
                text: "Die Zahlung ist fehlgeschlagen oder wurde abgebrochen."
            };

        case UI.ORDER_STATUS.EXPIRED:
            return {
                icon: <CancelIcon />,
                text: "Der Checkout ist abgelaufen, es wurde keine Zahlung durchgeführt."
            };

        case UI.ORDER_STATUS.REFUNDED:
            return {
                icon: <UndoIcon />,
                text: "Die Zahlung wurde rückerstattet."
            };

        case UI.ORDER_STATUS.DISPUTED:
            return {
                icon: <ReportProblemIcon />,
                text: "Die Zahlung wird derzeit angefochten (Dispute)."
            };
        case UI.ORDER_STATUS.DISPUTED_AND_REFUNDED:
            return {
                icon: <UndoIcon />,
                text: "Die Zahlung wurde angefochten und rückabgewickelt."
            };

        default:
            return {
                icon: <HelpOutlineIcon />,
                text: "Unbekannter Status."
            };
    }
};


/* =========================
   API (thin layer)
========================= */

async function apiFetch(url, options = {}) {
    const res = await fetch(url, options);
    if (!res.ok) {
        const text = await res.text().catch(() => "");
        const err = new Error(`HTTP ${res.status}`);
        err.status = res.status;
        err.body = text;
        throw err;
    }
    return res;
}

const api = {
    async login(emailAddress, password) {
        const params = new URLSearchParams({ emailAddress, password });
        const backendUrl = (await backendPool.pickBackend()).url;
        const res = await apiFetch(`${endpoints.login(backendUrl)}?${params.toString()}`, { method: "GET" });
        return res.json();
    },

    async fetchTasks(username, password, isAdmin, showAll) {
        let result = [];
        const params = withAuthParams(username, password);
        const availbleBackends = await backendPool.allAvailable();
        for (let index = 0; index < availbleBackends.length; index++) {
            const curBackend = availbleBackends[index];
            if (await backendPool.isAvailable(curBackend)) {
                const url = isAdmin && showAll ? `${endpoints.tasksAll(curBackend.url)}?${params}` : `${endpoints.tasksOwn(curBackend.url)}?${params}`;
                const curResult = await (await apiFetch(url)).json()
                const updatedResult = curResult.map(res => ({
                    ...res,
                    backendId: curBackend.id
                }));
                result = [...result, ...updatedResult]
            }
        }
        return result;
    },

    async abortTask(username, password, taskId, backendId) {
        const params = withAuthParams(username, password);
        const curBackend = backendPool.getById(backendId)
        const res = await apiFetch(`${endpoints.abortTask(curBackend.url, taskId)}?${params}`, { method: "DELETE" });
        return res.text();
    },

    async fetchResults(username, password, isAdmin, showAll) {
        const params = withAuthParams(username, password);
        const backend = await backendPool.pickBackend();
        const url = isAdmin && showAll ? `${endpoints.resultsAll(backend.url)}?${params}` : `${endpoints.resultsOwn(backend.url)}?${params}`;
        const res = await apiFetch(url);
        return res.json();
    },

    async previewResult(username, password, resultId) {
        const params = withAuthParams(username, password);
        const backend = await backendPool.pickBackend();
        const res = await apiFetch(`${endpoints.resultPreview(backend.url, resultId)}?${params}`);
        return res.text();
    },

    async exportResult(username, password, resultId, maxCount) {
        const params = withAuthParams(username, password, { maxCount });
        const backend = await backendPool.pickBackend();
        const res = await apiFetch(`${endpoints.resultExport(backend.url, resultId)}?${params}`);
        return res.text();
    },

    async exportCosts(username, password, resultId, maxCount) {
        const params = withAuthParams(username, password, { maxCount });
        const backend = await backendPool.pickBackend();
        const res = await apiFetch(`${endpoints.resultExportCosts(backend.url, resultId)}?${params}`);
        return res.json();
    },

    async fetchOrders(username, password, isAdmin, showAll) {
        const params = withAuthParams(username, password);
        const backend = await backendPool.pickBackend();
        const url = isAdmin && showAll ? `${endpoints.ordersAll(backend.url)}?${params}` : `${endpoints.ordersOwn(backend.url)}?${params}`;
        const res = await apiFetch(url);
        return res.json();
    },

    async downloadInvoice(username, password, orderId) {
        const params = withAuthParams(username, password);
        const backend = await backendPool.pickBackend();
        const res = await apiFetch(`${endpoints.orderInvoice(backend.url, orderId)}?${params}`);
        return res.blob();
    },

    async fetchPackages(username, password) {
        const params = withAuthParams(username, password);
        const backend = await backendPool.pickBackend();
        const res = await apiFetch(`${endpoints.orderPackages(backend.url)}?${params}`);
        return res.json();
    },

    async placeOrder(username, password, pkgName) {
        const params = withAuthParams(username, password, { name: pkgName });
        const backend = await backendPool.pickBackend();
        const res = await apiFetch(`${endpoints.orderPlace(backend.url)}?${params}`, { method: "PUT" });
        return res.text(); // redirect URL
    },

    async fetchProfile(username, password) {
        const params = withAuthParams(username, password);
        const backend = await backendPool.pickBackend();
        const res = await apiFetch(`${endpoints.profile(backend.url)}?${params}`);
        return res.json();
    },

    async fetchDescriptor(username, password, sourceName) {
        const params = withAuthParams(username, password);
        const backend = await backendPool.pickBackend();
        const res = await apiFetch(`${endpoints.sourceDescriptor(backend.url, sourceName)}?${params}`);
        return res.json();
    },

    async createAudience(queryUrl, body) {
        const res = await apiFetch(queryUrl, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });
        return res.json();
    },

    async fetchUsage(username, password, months = 12) {
        const params = withAuthParams(username, password, { months: months });
        const backend = await backendPool.pickBackend();
        const res = await apiFetch(`${endpoints.usage(backend.url)}?${params}`, { method: "GET" });
        return res.json();
    },


    /* =========================
       ADMIN – USER MANAGEMENT
    ========================= */

    async fetchUsers(username, password) {
        const params = withAuthParams(username, password);
        const backend = await backendPool.pickBackend();
        const res = await apiFetch(`${adminEndpoints.usersAll(backend.url)}?${params}`);
        return res.json(); // UserDTO[]
    },

    async setNoPayment(username, password, userId, value) {
        const params = withAuthParams(username, password);
        const backend = await backendPool.pickBackend();
        const res = await apiFetch(`${adminEndpoints.setNoPayment(backend.url, userId)}?${params}`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(value),
        });
        return res.json(); // UserDTO
    },

    async setSmallBusiness(username, password, userId, value) {
        const params = withAuthParams(username, password);
        const backend = await backendPool.pickBackend();
        const res = await apiFetch(`${adminEndpoints.setSmallBusiness(backend.url, userId)}?${params}`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(value),
        });
        return res.json();
    },

    async setCreditsBalance(username, password, userId, newBalance) {
        const params = withAuthParams(username, password);
        const backend = await backendPool.pickBackend();
        const res = await apiFetch(`${adminEndpoints.setCredits(backend.url, userId)}?${params}`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(newBalance),
        });
        return res.json();
    },

    async setAddress(username, password, userId, dto) {
        const params = withAuthParams(username, password);
        const backend = await backendPool.pickBackend();
        const res = await apiFetch(`${adminEndpoints.setAddress(backend.url, userId)}?${params}`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(dto),
        });
        return res.json();
    },

    async setBanking(username, password, userId, dto) {
        const params = withAuthParams(username, password);
        const backend = await backendPool.pickBackend();
        const res = await apiFetch(`${adminEndpoints.setBanking(backend.url, userId)}?${params}`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(dto),
        });
        return res.json();
    },

    async setTax(username, password, userId, dto) {
        const params = withAuthParams(username, password);
        const backend = await backendPool.pickBackend();
        const res = await apiFetch(`${adminEndpoints.setTax(backend.url, userId)}?${params}`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(dto),
        });
        return res.json();
    },
    async setBusinessName(username, password, userId, value) {
        const params = withAuthParams(username, password);
        const backend = await backendPool.pickBackend();
        const res = await apiFetch(`${adminEndpoints.setBusinessName(backend.url, userId)}?${params}`, {
            method: "PUT",
            headers: { "Content-Type": "text/plain;charset=UTF-8" },
            body: value,
        });
        return res.json();
    },

    async createUser(adminEmail, adminPassword, userEmailAddress, businessName) {
        const params = new URLSearchParams({
            emailAddress: adminEmail,
            password: adminPassword,
            userEmailAddress,
            businessName,
        });
        const backend = await backendPool.pickBackend();
        const res = await apiFetch(`${backend.url}/admin/user?${params.toString()}`, {
            method: "POST",
        });

        return res.json(); // UserDTO
    },
    async setUserPassword(username, password, userId, newPassword) {
        const params = withAuthParams(username, password, {
            newPassword,
        });
        const backend = await backendPool.pickBackend();
        await apiFetch(`${adminEndpoints.setPassword(backend.url, userId)}?${params}`, {
            method: "POST",
        });
    },
    async setDisabled(username, password, userId, value) {
        const params = withAuthParams(username, password, { disabled: value });
        const backend = await backendPool.pickBackend();
        const res = await apiFetch(
            `${adminEndpoints.setDisabled(backend.url, userId)}?${params}`,
            {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
            }
        );
        return res.json(); // UserDTO
    },

};

/* =========================
   REGEX Configuration
========================= */

const REGEX = {
    BUSINESS_NAME: /^.{1,100}$/,
    STREET: /^[A-Za-zÄÖÜäöüß ]{1,100}$/,
    HOUSE_NO: /^[0-9a-zA-Z/ -]{1,10}$/,
    ZIP: /^[0-9]{4,10}$/,
    TOWN: /^[0-9a-zA-Z/ -]{1,20}$/,
    COUNTRY: /^[A-Z_]{2,30}$/,
    BANK: /^$|^[ A-Za-zÄÖÜäöüß]{3,}$/,
    IBAN: /^$|^[A-Z]{2}[0-9A-Z]{13,32}$/,
    BIC: /^$|^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/,
    VAT_ID: /^$|^[A-Z]{2}[A-Z0-9]{2,12}$/,
    TAX_ID: /^$|^[0-9A-Z/-]{5,20}$/,
    PASSWORD: /^.{8,}$/,
    EMAIL: /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/
};

const ADMIN_USER_VALIDATION_FIELDS = [
    ["businessName", "businessName", REGEX.BUSINESS_NAME],
    ["street", "street", REGEX.STREET],
    ["houseNo", "houseNo", REGEX.HOUSE_NO],
    ["zipCode", "zipCode", REGEX.ZIP],
    ["town", "town", REGEX.TOWN],
    ["bank", "bank", REGEX.BANK],
    ["iban", "iban", REGEX.IBAN],
    ["bic", "bic", REGEX.BIC],
    ["vatId", "vatId", REGEX.VAT_ID],
    ["taxId", "taxId", REGEX.TAX_ID],
];

const getAdminUserValidationErrors = (values) => Object.fromEntries(
    ADMIN_USER_VALIDATION_FIELDS.map(([errorKey, draftKey, regex]) => [
        errorKey,
        !regex.test(values[draftKey]),
    ])
);


/* =========================
   PREMIUM PRODUCT THEME
========================= */

const premiumTheme = createTheme({
    palette: {
        mode: "light",
        primary: {
            main: UI.BRAND.PRIMARY_COLOR,
            contrastText: "#FFFFFF",
        },
        secondary: {
            main: "rgb(83, 64, 255)",
            contrastText: "#FFFFFF",
        },
        background: {
            default: "#FFFFFF",
            paper: "#FFFFFF",
        },
    },
    shape: {
        borderRadius: 4,
    },
    components: {
        MuiCssBaseline: {
            styleOverrides: {
                html: {
                    overflowY: "scroll",
                    overflowX: "hidden",
                    scrollbarGutter: "stable",
                    WebkitTextSizeAdjust: "100%",
                },
                body: {
                    overflowY: "scroll",
                    overflowX: "hidden",
                    scrollbarGutter: "stable",
                    WebkitTextSizeAdjust: "100%",
                },
                "#root": {
                    minHeight: "100vh",
                    overflowX: "hidden",
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: { backgroundImage: "none" },
            },
        },
        MuiButton: {
            defaultProps: {
                variant: "contained",
                color: "secondary",
                disableElevation: true,
            },
            styleOverrides: {
                root: {
                    minHeight: 42,
                    paddingLeft: 20,
                    paddingRight: 20,
                    lineHeight: 1.25,
                    whiteSpace: "nowrap",
                    "@media (max-width:600px)": {
                        minHeight: 44,
                        paddingLeft: 16,
                        paddingRight: 16,
                        whiteSpace: "normal",
                        textAlign: "center",
                    },
                },
                sizeSmall: {
                    minHeight: 38,
                    paddingLeft: 16,
                    paddingRight: 16,
                    "@media (max-width:600px)": {
                        minHeight: 40,
                        paddingLeft: 14,
                        paddingRight: 14,
                    },
                },
                containedSecondary: {
                    backgroundColor: "rgb(83, 64, 255)",
                    "&:hover": {
                        backgroundColor: UI.BRAND.PRIMARY_COLOR,
                    },
                },
            },
        },
        MuiDialog: {
            styleOverrides: {
                root: {
                    "@media (max-width:600px)": {
                        "& .MuiDialog-paper:not(.MuiDialog-paperFullScreen)": {
                            margin: 12,
                            width: "calc(100% - 24px)",
                            maxHeight: "calc(100% - 24px)",
                        },
                    },
                },
            },
        },
    },
});

const PREMIUM_SECONDARY_ACTION_SX = {
    boxShadow: "0 6px 16px rgba(83, 64, 255, 0.20)",
    transition: "transform .16s ease, box-shadow .16s ease, background-color .16s ease",
    "&:hover": {
        boxShadow: "0 9px 22px rgba(12, 5, 109, 0.20)",
        transform: "translateY(-1px)",
    },
    "&:active": {
        boxShadow: "0 3px 9px rgba(83, 64, 255, 0.16)",
        transform: "translateY(0)",
    },
    "&.Mui-disabled": {
        boxShadow: "none",
        transform: "none",
    },
};

/* =========================
   PRODUCT-LIKE UI BUILDING BLOCKS
========================= */

function ResponsiveDialog({ fullScreen = false, ...props }) {
    const isMobile = useMediaQuery("(max-width:599.95px)", { noSsr: true });

    return (
        <Dialog
            {...props}
            fullScreen={Boolean(fullScreen) || isMobile}
        />
    );
}


function PageHeader({ title, icon }) {
    return (
        <Paper
            sx={{
                p: { xs: "18px", sm: "25px" },
                backgroundColor: UI.BRAND.PRIMARY_COLOR,
                color: "white",
                display: "flex",
                alignItems: "center",
                gap: 2,
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                {icon ? (
                    <Box sx={{ color: "white", display: "grid", placeItems: "center", "& svg": { fontSize: 30 } }}>
                        {icon}
                    </Box>
                ) : null}
                <Typography variant="h6" component="h1">
                    {title}
                </Typography>
            </Box>
        </Paper>
    );
}


function EmptyState({ title, subtitle, action }) {
    return (
        <Box sx={{ py: 2 }}>
            <Typography variant="body1">{title}</Typography>
            {subtitle ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                    {subtitle}
                </Typography>
            ) : null}
            {action ? <Box sx={{ mt: 2 }}>{action}</Box> : null}
        </Box>
    );
}

function CardSkeletonList({ rows = 5 }) {
    return (
        <Box>
            {Array.from({ length: rows }).map((_, idx) => (
                <Paper
                    key={idx}
                    variant="outlined"
                    sx={{
                        borderRadius: 3,
                        p: "25px",
                        mb: 2,
                        borderColor: "rgba(12,5,109,.14)",
                        boxShadow: "0 12px 30px rgba(12,5,109,.06)",
                    }}
                >
                    <Skeleton width="38%" height={28} />
                    <Skeleton width="58%" />
                </Paper>
            ))}
        </Box>
    );
}

/* =========================
   HELP CENTER (NEW)
========================= */

function HelpCenterDrawer({ open, onClose, showSnackbar, onOpenSupport }) {
    const [query, setQuery] = useState("");
    const [expanded, setExpanded] = useState(null);

    useEffect(() => {
        if (!open) return;
        setQuery("");
        setExpanded(null);
    }, [open]);

    const allArticles = useMemo(() => {
        const items = [];
        for (const section of HELP_DOCS.sections) {
            for (const article of section.articles) {
                items.push({
                    sectionId: section.id,
                    sectionTitle: section.title,
                    sectionDescription: section.description,
                    ...article,
                });
            }
        }
        return items;
    }, []);

    const normalizedQuery = query.trim().toLowerCase();

    const filteredSections = useMemo(() => {
        if (!normalizedQuery) return HELP_DOCS.sections;

        const matchesArticle = (article) => {
            const hay = [
                article.title,
                ...(article.tags || []),
                ...(article.body || []),
            ]
                .join(" ")
                .toLowerCase();

            return hay.includes(normalizedQuery);
        };

        return HELP_DOCS.sections
            .map((section) => ({
                ...section,
                articles: section.articles.filter(matchesArticle),
            }))
            .filter((section) => section.articles.length > 0);
    }, [normalizedQuery]);

    const totalHits = useMemo(() => {
        if (!normalizedQuery) return allArticles.length;
        return filteredSections.reduce((acc, s) => acc + s.articles.length, 0);
    }, [filteredSections, normalizedQuery, allArticles.length]);

    const handleCopy = async (text) => {
        const ok = await copyToClipboard(text);
        showSnackbar(ok ? UI.MESSAGES.HELP_COPY_OK : UI.MESSAGES.HELP_COPY_FAIL, ok ? "success" : "error");
    };

    const drawerHeader = (
        <Box
            sx={{
                px: { xs: 2, sm: 2.5 },
                pt: { xs: "calc(18px + env(safe-area-inset-top))", sm: 2.5 },
                pb: 2,
                bgcolor: "background.paper",
                borderBottom: "1px solid rgba(12,5,109,.10)",
            }}
        >
            <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2 }}>
                <Box sx={{ minWidth: 0 }}>
                    <Typography variant="h6" sx={{ fontWeight: 500, color: "text.primary" }}>
                        {UI.HELP.TITLE}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
                        {UI.HELP.SUBTITLE}
                    </Typography>
                </Box>
                <IconButton onClick={onClose} aria-label={UI.HELP.CLOSE} sx={{ color: "text.secondary", flexShrink: 0 }}>
                    <CloseIcon />
                </IconButton>
            </Box>

            <Box sx={{ mt: 2 }}>
                <TextField
                    fullWidth
                    size="small"
                    label={UI.LABELS.HELP_SEARCH}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    inputProps={{ "aria-label": UI.ARIA.HELP_SEARCH }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                    }}
                />
                <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                        {UI.LABELS.HELP_RESULTS(totalHits)}
                    </Typography>
                </Box>
            </Box>
        </Box>
    );

    const tips = (
        <Paper variant="outlined" sx={{ borderRadius: UI.LAYOUT.CARD_RADIUS, p: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {UI.LABELS.HELP_TIPS_TITLE}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                • {UI.LABELS.HELP_TIPS_1}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                • {UI.LABELS.HELP_TIPS_2}
            </Typography>
        </Paper>
    );

    const contact = (
        <Paper variant="outlined" sx={{ borderRadius: UI.LAYOUT.CARD_RADIUS, p: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {UI.HELP.CONTACT_TITLE}
            </Typography>

            <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary">
                    {UI.SUPPORT.LABEL_EMAIL}
                </Typography>
                <Box sx={{ display: "flex", gap: 1, alignItems: "center", mt: 0.25, flexWrap: "wrap" }}>
                    <Typography component="a" href={`mailto:${UI.SUPPORT.EMAIL}`} sx={{ textDecoration: "none", color: "primary.main" }}>
                        {UI.SUPPORT.EMAIL}
                    </Typography>
                    <Button size="small" variant="contained" onClick={() => handleCopy(UI.SUPPORT.EMAIL)}>
                        Copy
                    </Button>
                </Box>
            </Box>

            <Box sx={{ mt: 1.5 }}>
                <Typography variant="caption" color="text.secondary">
                    {UI.SUPPORT.LABEL_PHONE}
                </Typography>
                <Box sx={{ display: "flex", gap: 1, alignItems: "center", mt: 0.25, flexWrap: "wrap" }}>
                    <Typography component="a" href={`tel:${UI.SUPPORT.PHONE.replace(/\s/g, "")}`} sx={{ textDecoration: "none", color: "primary.main" }}>
                        {UI.SUPPORT.PHONE}
                    </Typography>
                    <Button size="small" variant="contained" onClick={() => handleCopy(UI.SUPPORT.PHONE)}>
                        Copy
                    </Button>
                </Box>
            </Box>

            <Box sx={{ mt: 1.5 }}>
                <Button variant="contained" onClick={onOpenSupport} startIcon={<HelpOutlineIcon />}>
                    {UI.MENUS.SUPPORT}
                </Button>
            </Box>
        </Paper>
    );

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    width: { xs: "100vw", sm: UI.LAYOUT.HELP_DRAWER_WIDTH },
                    maxWidth: "100vw",
                    pb: "env(safe-area-inset-bottom)",
                },
            }}
        >
            {drawerHeader}

            <Box sx={{ p: { xs: 2, sm: 2.5 }, display: "flex", flexDirection: "column", gap: 1.5, overflowY: "auto", height: "100%" }}>
                {tips}

                {filteredSections.length === 0 ? (
                    <EmptyState title={UI.LABELS.HELP_EMPTY_SEARCH} subtitle="" />
                ) : (
                    <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                            {UI.HELP.SECTION_LABEL}
                        </Typography>

                        {filteredSections.map((section) => (
                            <Accordion
                                key={section.id}
                                expanded={expanded === section.id}
                                onChange={(_, isExpanded) => setExpanded(isExpanded ? section.id : null)}
                            >
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                            {section.title}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {section.description}
                                        </Typography>
                                    </Box>
                                </AccordionSummary>

                                <AccordionDetails>
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                        {section.articles.map((article) => (
                                            <Paper
                                                key={article.id}
                                                variant="outlined"
                                                sx={{ borderRadius: UI.LAYOUT.CARD_RADIUS, p: 1.5 }}
                                            >
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                    <ArticleIcon fontSize="small" />
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                        {article.title}
                                                    </Typography>
                                                </Box>

                                                <Box sx={{ mt: 1 }}>
                                                    {article.body.map((line, idx) => (
                                                        <Typography key={idx} variant="body2" color="text.secondary" sx={{ mt: idx === 0 ? 0 : 0.6 }}>
                                                            {line}
                                                        </Typography>
                                                    ))}
                                                </Box>

                                                {article.tags?.length ? (
                                                    <Box sx={{ mt: 1.25, display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                                                        {article.tags.map((t) => (
                                                            <Chip key={t} size="small" variant="outlined" label={t} />
                                                        ))}
                                                    </Box>
                                                ) : null}
                                            </Paper>
                                        ))}
                                    </Box>
                                </AccordionDetails>
                            </Accordion>
                        ))}
                    </Box>
                )}

                {contact}
            </Box>
        </Drawer>
    );
}

/* =========================
   DIALOGS
========================= */

function SupportContactDialog({ open, onClose }) {
    return (
        <ResponsiveDialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>{UI.DIALOGS.SUPPORT_TITLE}</DialogTitle>

            <DialogContent dividers>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                    <Typography variant="body2" color="text.secondary">
                        {UI.SUPPORT.INTRO}
                    </Typography>

                    <Divider />

                    <Typography variant="subtitle2">{UI.SUPPORT.LABEL_EMAIL}</Typography>
                    <Typography
                        component="a"
                        href={`mailto:${UI.SUPPORT.EMAIL}?subject=${encodeURIComponent(UI.SUPPORT.SUBJECT)}`}
                        sx={{ color: "primary.main", textDecoration: "none" }}
                    >
                        {UI.SUPPORT.EMAIL}
                    </Typography>

                    <Typography variant="subtitle2" sx={{ mt: 1 }}>
                        {UI.SUPPORT.LABEL_PHONE}
                    </Typography>
                    <Typography
                        component="a"
                        href={`tel:${UI.SUPPORT.PHONE.replace(/\s/g, "")}`}
                        sx={{ color: "primary.main", textDecoration: "none" }}
                    >
                        {UI.SUPPORT.PHONE}
                    </Typography>
                </Box>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>{UI.BUTTONS.CLOSE}</Button>
            </DialogActions>
        </ResponsiveDialog>
    );
}

function UserProfileDialog({ open, onClose, username, password, showSnackbar }) {
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open) return;

        let alive = true;
        (async () => {
            setLoading(true);
            try {
                const data = await api.fetchProfile(username, password);
                if (!alive) return;
                setProfile(data);
            } catch {
                showSnackbar(UI.MESSAGES.PROFILE_LOAD_ERROR, "error");
            } finally {
                if (alive) setLoading(false);
            }
        })();

        return () => {
            alive = false;
        };
    }, [open, username, password, showSnackbar]);

    const displayValue = (value) => {
        if (value === null || value === undefined || String(value).trim() === "") return "–";
        return value;
    };

    const profileSections = profile ? [
        {
            title: "Unternehmen & Kontakt",
            Icon: WorkOutlineIcon,
            items: [
                { label: "Firmenname", value: displayValue(profile.businessName) },
                { label: "E-Mail-Adresse", value: displayValue(profile.emailAddress) },
            ],
        },
        {
            title: "Anschrift",
            Icon: LocationOnIcon,
            items: [
                {
                    label: "Adresse",
                    value: [profile.street, profile.houseNo].filter(Boolean).join(" ") || "–",
                },
                {
                    label: "Ort",
                    value: [profile.zipCode, profile.town].filter(Boolean).join(" ") || "–",
                },
                { label: "Land", value: displayValue(profile.country) },
            ],
        },
        {
            title: "Abrechnung",
            Icon: AccountBalanceIcon,
            items: [
                { label: "Bank", value: displayValue(profile.bank) },
                { label: "IBAN", value: displayValue(profile.iban) },
                { label: "BIC", value: displayValue(profile.bic) },
                { label: UI.LABELS.TAX_ID, value: displayValue(profile.taxId) },
                { label: UI.LABELS.VAT_ID, value: displayValue(profile.vatId) },
            ],
        },
    ] : [];

    return (
        <ResponsiveDialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{ sx: { borderRadius: 2, overflow: "hidden" } }}
        >
            <AppBar position="relative" elevation={0} sx={{ bgcolor: UI.BRAND.PRIMARY_COLOR }}>
                <Toolbar>
                    <IconButton edge="start" color="inherit" onClick={onClose} aria-label={UI.BUTTONS.CLOSE}>
                        <CloseIcon />
                    </IconButton>
                    <Typography variant="h6" sx={{ ml: 2, flex: 1, fontWeight: 500 }}>
                        Mein Profil
                    </Typography>
                </Toolbar>
            </AppBar>

            <DialogContent sx={{ bgcolor: "background.default", p: { xs: 2.5, sm: 4 } }}>
                {loading && (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                        <CircularProgress />
                    </Box>
                )}

                {!loading && profile && (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                        <Paper
                            variant="outlined"
                            sx={{
                                p: { xs: 2.5, sm: 3 },
                                borderColor: "rgba(12,5,109,.14)",
                                boxShadow: "0 12px 30px rgba(12,5,109,.06)",
                                borderRadius: 2,
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: { xs: "flex-start", sm: "center" },
                                    justifyContent: "space-between",
                                    flexDirection: { xs: "column", sm: "row" },
                                    gap: 2.5,
                                }}
                            >
                                <Box sx={{ display: "flex", alignItems: "center", gap: 2, minWidth: 0 }}>
                                    <Box
                                        sx={{
                                            width: 52,
                                            height: 52,
                                            borderRadius: 2,
                                            display: "grid",
                                            placeItems: "center",
                                            bgcolor: "rgba(12,5,109,.08)",
                                            color: "primary.main",
                                            flexShrink: 0,
                                        }}
                                    >
                                        <AccountCircleIcon sx={{ fontSize: 30 }} />
                                    </Box>
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography variant="h6" sx={{ fontWeight: 500, overflowWrap: "anywhere" }}>
                                            {displayValue(profile.businessName)}
                                        </Typography>
                                        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap", mt: 0.6 }}>
                                            <Chip
                                                size="small"
                                                variant="outlined"
                                                label={profile.admin ? "Administrator" : "Benutzer"}
                                                sx={{ borderColor: "rgba(12,5,109,.22)", color: "primary.main" }}
                                            />
                                            <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
                                                {displayValue(profile.emailAddress)}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>

                                <Box sx={{ minWidth: { sm: 170 }, textAlign: { xs: "left", sm: "right" } }}>
                                    <Typography variant="caption" color="text.secondary">
                                        Credit-Guthaben
                                    </Typography>
                                    <Typography
                                        variant="h5"
                                        sx={{
                                            mt: 0.3,
                                            fontWeight: 500,
                                            color: "primary.main",
                                            fontVariantNumeric: "tabular-nums",
                                        }}
                                    >
                                        {profile.creditsBalance ?? 0}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        verfügbare Credits
                                    </Typography>
                                </Box>
                            </Box>
                        </Paper>

                        <Grid container spacing={2.5}>
                            {profileSections.map(({ title, Icon, items }) => (
                                <Grid item xs={12} md={title === "Abrechnung" ? 12 : 6} key={title}>
                                    <Paper
                                        variant="outlined"
                                        sx={{
                                            height: "100%",
                                            p: { xs: 2.25, sm: 2.75 },
                                            borderRadius: 2,
                                            borderColor: "rgba(12,5,109,.12)",
                                            boxShadow: "0 8px 24px rgba(12,5,109,.04)",
                                        }}
                                    >
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2.25 }}>
                                            <Box
                                                sx={{
                                                    width: 34,
                                                    height: 34,
                                                    borderRadius: 1.5,
                                                    display: "grid",
                                                    placeItems: "center",
                                                    bgcolor: "rgba(12,5,109,.07)",
                                                    color: "primary.main",
                                                }}
                                            >
                                                <Icon fontSize="small" />
                                            </Box>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                                                {title}
                                            </Typography>
                                        </Box>

                                        <Grid container spacing={2}>
                                            {items.map((item) => (
                                                <Grid item xs={12} sm={title === "Abrechnung" ? 6 : 12} key={`${title}-${item.label}`}>
                                                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.35 }}>
                                                        {item.label}
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ overflowWrap: "anywhere", lineHeight: 1.55 }}>
                                                        {item.value}
                                                    </Typography>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </Paper>
                                </Grid>
                            ))}

                            <Grid item xs={12}>
                                <Paper
                                    variant="outlined"
                                    sx={{
                                        p: { xs: 2.25, sm: 2.75 },
                                        borderRadius: 2,
                                        borderColor: "rgba(12,5,109,.12)",
                                        boxShadow: "0 8px 24px rgba(12,5,109,.04)",
                                    }}
                                >
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                                        <Box
                                            sx={{
                                                width: 34,
                                                height: 34,
                                                borderRadius: 1.5,
                                                display: "grid",
                                                placeItems: "center",
                                                bgcolor: "rgba(12,5,109,.07)",
                                                color: "primary.main",
                                            }}
                                        >
                                            <LockIcon fontSize="small" />
                                        </Box>
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                                                Zugriff & Sicherheit
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.3 }}>
                                                Rolle: {profile.admin ? "Administrator" : "Benutzer"}. Passwortänderungen werden derzeit administrativ verwaltet.
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Paper>
                            </Grid>
                        </Grid>
                    </Box>
                )}
            </DialogContent>
        </ResponsiveDialog>
    );
}

const SUBTASK_NAME_TRANSLATION_DICT = {
    PROBING: "Recherche vorbereiten",
    DISCOVERY: "Unternehmen identifizieren",
    WEB_ANALYSIS: "Online-Präsenz prüfen",
    COMMERCIAL_REGISTER: "Offizielle Register abgleichen",
    FINALIZATION: "Ergebnisse aufbereiten",
};

const clampProgress = (value) => Math.max(0, Math.min(100, Number(value) || 0));

const getOverallTaskProgress = (task) => {
    const subtasks = task?.subtaskList || [];
    if (!subtasks.length) return 0;
    return Math.round(subtasks.reduce((sum, subtask) => sum + clampProgress(subtask.progress), 0) / subtasks.length);
};

const getResearchStageMeta = (subtask) => {
    const status = subtask?.status;
    if (status === UI.STATUS.TASK.COMPLETED) return { label: "Abgeschlossen", tone: "success" };
    if (status === UI.STATUS.TASK.RUNNING) return { label: "In Bearbeitung", tone: "primary" };
    if (status === UI.STATUS.TASK.FAILED) return { label: "Fehlgeschlagen", tone: "error" };
    if (status === UI.STATUS.TASK.ABORTED) return { label: "Abgebrochen", tone: "error" };
    return { label: "Ausstehend", tone: "neutral" };
};

function ResearchProgressVisualization({ task, compact = false }) {
    const [expandedStageIndex, setExpandedStageIndex] = useState(null);
    const subtasks = task?.subtaskList || [];
    const overall = getOverallTaskProgress(task);
    const activeIndex = subtasks.findIndex((subtask) => subtask.status === UI.STATUS.TASK.RUNNING);
    const activeSubtask = activeIndex >= 0 ? subtasks[activeIndex] : null;
    const completedCount = subtasks.filter((subtask) => subtask.status === UI.STATUS.TASK.COMPLETED).length;

    const activeStageName = activeSubtask
        ? (SUBTASK_NAME_TRANSLATION_DICT[activeSubtask.name] ?? activeSubtask.name)
        : overall >= 100
            ? "Ergebnisse aufbereitet"
            : "Recherche wird vorbereitet";

    return (
        <Box sx={{ width: "100%" }}>
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "minmax(0, 1fr) auto" },
                    gap: { xs: 2.5, sm: 4 },
                    alignItems: "center",
                }}
            >
                <Box sx={{ minWidth: 0 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.35 }}>
                        <Typography variant="body2" color="text.secondary">
                            Aktuelle Research-Phase
                        </Typography>
                        <Tooltip
                            arrow
                            title={
                                activeSubtask
                                    ? "ATLAS verarbeitet diese Phase aktuell. Den Detailfortschritt jeder Phase sehen Sie direkt beim Darüberfahren."
                                    : overall >= 100
                                        ? "Alle Research-Phasen wurden verarbeitet."
                                        : "Der Auftrag wartet auf die nächste verfügbare Research-Phase."
                            }
                        >
                            <IconButton
                                size="small"
                                aria-label="Information zum Research-Fortschritt"
                                sx={{
                                    width: 24,
                                    height: 24,
                                    color: "text.secondary",
                                    opacity: 0.72,
                                    "&:hover": { opacity: 1, bgcolor: "rgba(12,5,109,.05)" },
                                }}
                            >
                                <InfoIcon sx={{ fontSize: 15 }} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                    <Typography
                        variant={compact ? "h6" : "h5"}
                        component="div"
                        sx={{ mt: 0.35, lineHeight: 1.25, overflowWrap: "anywhere" }}
                    >
                        {activeStageName}
                    </Typography>
                </Box>

                <Box
                    sx={{
                        position: "relative",
                        width: compact ? 76 : 92,
                        height: compact ? 76 : 92,
                        display: "grid",
                        placeItems: "center",
                        justifySelf: { xs: "start", sm: "end" },
                    }}
                >
                    <CircularProgress
                        variant="determinate"
                        value={100}
                        size={compact ? 76 : 92}
                        thickness={3.2}
                        sx={{ position: "absolute", color: "rgba(12,5,109,.10)" }}
                    />
                    <CircularProgress
                        variant="determinate"
                        value={overall}
                        size={compact ? 76 : 92}
                        thickness={3.2}
                        sx={{
                            position: "absolute",
                            color: "primary.main",
                            "& .MuiCircularProgress-circle": { strokeLinecap: "round" },
                        }}
                    />
                    <Box sx={{ textAlign: "center" }}>
                        <Typography variant={compact ? "subtitle1" : "h6"} sx={{ lineHeight: 1.05 }}>
                            {overall}%
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Gesamt
                        </Typography>
                    </Box>
                </Box>
            </Box>

            <Box sx={{ mt: compact ? 2.5 : 3.5 }}>
                <LinearProgress
                    variant="determinate"
                    value={overall}
                    sx={{
                        height: 6,
                        bgcolor: "rgba(12,5,109,.08)",
                        "& .MuiLinearProgress-bar": {
                            bgcolor: "primary.main",
                        },
                    }}
                />
                <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.8, gap: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                        Research-Fortschritt
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {completedCount} von {subtasks.length} Phasen abgeschlossen
                    </Typography>
                </Box>
            </Box>

            <Box
                sx={{
                    mt: compact ? 2.6 : 3.5,
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: `repeat(${Math.max(subtasks.length, 1)}, minmax(0, 1fr))` },
                    gap: { xs: 0.75, md: 0 },
                }}
            >
                {subtasks.map((subtask, index) => {
                    const progress = clampProgress(subtask.progress);
                    const meta = getResearchStageMeta(subtask);
                    const active = subtask.status === UI.STATUS.TASK.RUNNING;
                    const completed = subtask.status === UI.STATUS.TASK.COMPLETED;
                    const failed = subtask.status === UI.STATUS.TASK.FAILED || subtask.status === UI.STATUS.TASK.ABORTED;
                    const stageName = SUBTASK_NAME_TRANSLATION_DICT[subtask.name] ?? subtask.name;

                    const stageTooltip = (
                        <Box sx={{ minWidth: 220, p: 0.5 }}>
                            <Typography variant="body2" sx={{ color: "inherit" }}>
                                {stageName}
                            </Typography>
                            <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, mt: 0.75 }}>
                                <Typography variant="caption" sx={{ color: "inherit", opacity: 0.75 }}>
                                    {meta.label}
                                </Typography>
                                <Typography variant="body2" sx={{ color: "inherit" }}>
                                    {progress}%
                                </Typography>
                            </Box>
                            <LinearProgress
                                variant="determinate"
                                value={progress}
                                sx={{
                                    mt: 0.9,
                                    height: 4,
                                    bgcolor: "rgba(255,255,255,.22)",
                                    "& .MuiLinearProgress-bar": { bgcolor: "white" },
                                }}
                            />
                        </Box>
                    );

                    return (
                        <Tooltip
                            key={subtask.stage ?? subtask.name ?? index}
                            title={stageTooltip}
                            arrow
                            placement="top"
                            enterDelay={120}
                            enterTouchDelay={120}
                            leaveTouchDelay={4000}
                        >
                            <Box
                                role="button"
                                tabIndex={0}
                                aria-expanded={expandedStageIndex === index}
                                onClick={() => setExpandedStageIndex((current) => current === index ? null : index)}
                                onKeyDown={(event) => {
                                    if (event.key === "Enter" || event.key === " ") {
                                        event.preventDefault();
                                        setExpandedStageIndex((current) => current === index ? null : index);
                                    }
                                }}
                                sx={{
                                    position: "relative",
                                    display: { xs: "grid", md: "block" },
                                    gridTemplateColumns: "36px minmax(0,1fr) auto",
                                    alignItems: "center",
                                    gap: 1.25,
                                    px: { xs: 1, md: 1.15 },
                                    py: { xs: 1.25, md: 1.5 },
                                    borderRadius: 2,
                                    minWidth: 0,
                                    cursor: { xs: "pointer", md: "help" },
                                    transition: "background-color .15s ease",
                                    "&:hover": { bgcolor: "rgba(12,5,109,.035)" },
                                    "&:hover .research-stage-dot": {
                                        transform: "translateY(-2px)",
                                        boxShadow: "0 0 0 5px rgba(12,5,109,.08)",
                                    },
                                }}
                            >
                                {index < subtasks.length - 1 ? (
                                    <Box
                                        sx={{
                                            display: { xs: "none", md: "block" },
                                            position: "absolute",
                                            top: 30,
                                            left: "calc(50% + 18px)",
                                            right: "calc(-50% + 18px)",
                                            height: 2,
                                            bgcolor: completed ? "primary.main" : "rgba(12,5,109,.12)",
                                            zIndex: 0,
                                        }}
                                    />
                                ) : null}

                                <Box
                                    className="research-stage-dot"
                                    sx={{
                                        position: "relative",
                                        zIndex: 1,
                                        width: 36,
                                        height: 36,
                                        mx: { xs: 0, md: "auto" },
                                        borderRadius: "50%",
                                        display: "grid",
                                        placeItems: "center",
                                        bgcolor: completed ? "primary.main" : failed ? "error.main" : active ? "white" : "#F5F5F8",
                                        color: completed || failed ? "white" : active ? "primary.main" : "text.secondary",
                                        border: active ? "2px solid" : "1px solid",
                                        borderColor: failed ? "error.main" : completed || active ? "primary.main" : "rgba(12,5,109,.22)",
                                        boxShadow: active ? "0 0 0 5px rgba(12,5,109,.08)" : "none",
                                        transition: "transform .15s ease, box-shadow .15s ease",
                                    }}
                                >
                                    {completed ? (
                                        <DoneIcon sx={{ fontSize: 18 }} />
                                    ) : active ? (
                                        <LoopIcon sx={{ fontSize: 18 }} />
                                    ) : failed ? (
                                        <ErrorOutlineIcon sx={{ fontSize: 18 }} />
                                    ) : (
                                        <Typography variant="caption">{index + 1}</Typography>
                                    )}
                                </Box>

                                <Box sx={{ mt: { xs: 0, md: 1.15 }, textAlign: { xs: "left", md: "center" }, minWidth: 0 }}>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            display: "block",
                                            color: active ? "primary.main" : "text.primary",
                                            lineHeight: 1.3,
                                            overflowWrap: "anywhere",
                                        }}
                                    >
                                        {stageName}
                                    </Typography>
                                    {!compact ? (
                                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.35 }}>
                                            {meta.label}
                                        </Typography>
                                    ) : null}
                                </Box>

                                <Typography
                                    variant="caption"
                                    sx={{
                                        display: { xs: "block", md: "none" },
                                        color: active ? "primary.main" : "text.secondary",
                                    }}
                                >
                                    {progress}%
                                </Typography>

                                {expandedStageIndex === index ? (
                                    <Box
                                        sx={{
                                            display: { xs: "block", md: "none" },
                                            gridColumn: "1 / -1",
                                            ml: 5.9,
                                            mr: 0.5,
                                            mt: 0.25,
                                            pb: 0.5,
                                        }}
                                    >
                                        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, mb: 0.65 }}>
                                            <Typography variant="caption" color="text.secondary">
                                                {meta.label}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: active ? "primary.main" : "text.secondary", fontVariantNumeric: "tabular-nums" }}>
                                                {progress}%
                                            </Typography>
                                        </Box>
                                        <LinearProgress
                                            variant="determinate"
                                            value={progress}
                                            sx={{
                                                height: 4,
                                                bgcolor: "rgba(12,5,109,.08)",
                                                "& .MuiLinearProgress-bar": { bgcolor: "primary.main" },
                                            }}
                                        />
                                    </Box>
                                ) : null}
                            </Box>
                        </Tooltip>
                    );
                })}
            </Box>
        </Box>
    );
}

function OrderPackageDialog({ open, onClose, username, password, showSnackbar }) {
    const [packages, setPackages] = React.useState([]);
    const [loading, setLoading] = React.useState(false);

    const loadPackages = React.useCallback(async () => {
        setLoading(true);
        try {
            const data = await api.fetchPackages(username, password);

            const withPrices = data.map((pkg) => {
                const safeDiscount = Math.min(100, Math.max(0, Number(pkg.discount ?? 0)));

                const fullTotal = (Number(pkg.pricePerCredit) || 0) * (Number(pkg.count) || 0);
                const discountedTotal = Math.round(fullTotal * (100 - safeDiscount) / 100);

                return {
                    ...pkg,
                    fullPrice: fullTotal,
                    price: discountedTotal,
                };
            });

            setPackages(withPrices);
        } catch {
            showSnackbar(UI.MESSAGES.PACKAGES_LOAD_ERROR, "error");
            setPackages([]);
        } finally {
            setLoading(false);
        }
    }, [username, password, showSnackbar]);

    React.useEffect(() => {
        if (open) loadPackages();
    }, [open, loadPackages]);

    const handleSelectPackage = React.useCallback(
        async (pkg) => {
            try {
                const redirectUrl = await api.placeOrder(username, password, pkg.name);
                window.open(redirectUrl, "_blank", "noopener,noreferrer");
            } catch {
                showSnackbar(UI.MESSAGES.PAYMENT_REDIRECT_ERROR, "error");
            } finally {
                onClose();
            }
        },
        [username, password, showSnackbar, onClose]
    );

    return (
        <ResponsiveDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Credits für Ihren Research-Workspace</DialogTitle>

            <DialogContent dividers>
                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: UI.LAYOUT.CARD_GAP,
                        }}
                    >
                        {packages.map((pkg) => {
                            const hasDiscount = Number(pkg.discount ?? 0) > 0 && pkg.fullPrice > pkg.price;

                            return (
                                <Paper
                                    key={pkg.name}
                                    variant="outlined"
                                    sx={{
                                        borderRadius: 3,
                                        p: 2.5,
                                        cursor: "pointer",
                                        borderColor: "rgba(23,17,92,.09)",
                                        boxShadow: "0 8px 26px rgba(23,17,92,.04)",
                                        transition: "all .18s ease",
                                        "&:hover": { transform: "translateY(-2px)", borderColor: "rgba(23,17,92,.20)", boxShadow: "0 16px 38px rgba(23,17,92,.09)" },
                                    }}
                                    onClick={() => handleSelectPackage(pkg)}
                                >
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            gap: 2,
                                        }}
                                    >
                                        <Box>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                                {pkg.name}
                                            </Typography>

                                            <Typography variant="body2" color="text.secondary">
                                                {pkg.count} Credits
                                            </Typography>

                                            {/* Rabattierter Preis (wird angezeigt) */}
                                            <Typography variant="body2" sx={{ mt: 0.75, fontWeight: 700 }}>
                                                {formatEuroFromCents(pkg.price)}
                                            </Typography>

                                            <Typography variant="caption" color="text.secondary">
                                                {formatCentsPerCredit(pkg.price, pkg.count)}
                                            </Typography>

                                            {/* Optional: vollen Preis + Prozent anzeigen */}
                                            {hasDiscount && (
                                                <Box sx={{ mt: 0.25 }}>
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        sx={{ textDecoration: "line-through", display: "block" }}
                                                    >
                                                        {formatEuroFromCents(pkg.fullPrice)}
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        color="success.main"
                                                        sx={{ display: "block" }}
                                                    >
                                                        −{pkg.discount}% Rabatt
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>

                                        <Button variant="contained" sx={PREMIUM_SECONDARY_ACTION_SX}>{UI.BUTTONS.BUY_PACKAGES}</Button>
                                    </Box>
                                </Paper>
                            );
                        })}
                    </Box>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>{UI.BUTTONS.CANCEL}</Button>
            </DialogActions>
        </ResponsiveDialog>
    );
}

function ExportDialog({ open, onClose, result, username, password, showSnackbar, onBuyCredits }) {
    const maxAvailable = result?.size ?? 0;

    const [count, setCount] = useState(1);
    const [userCredits, setUserCredits] = useState(0);
    const [requiredCredits, setRequiredCredits] = useState(null);
    const [loadingCosts, setLoadingCosts] = useState(false);

    const creditsEnough = requiredCredits != null && requiredCredits <= userCredits;

    useEffect(() => {
        if (!open || !result) return;
        setCount(Math.min(1000, maxAvailable));
    }, [open, result, maxAvailable]);


    useEffect(() => {
        if (!open)
            return;
        (async () => {
            try {
                const data = await api.fetchProfile(username, password);
                setUserCredits(data.creditsBalance);
            } catch {
                showSnackbar(UI.MESSAGES.CREDIT_LOAD_ERROR, "error");
            }
        })();
    }, [open, username, password, showSnackbar]);

    useEffect(() => {
        if (!open || !result || count < 1) return;

        let alive = true;
        (async () => {
            setLoadingCosts(true);
            try {
                const costs = await api.exportCosts(username, password, result.id, count);
                if (!alive) return;
                setRequiredCredits(costs);
            } catch (e) {
                showSnackbar(UI.MESSAGES.COSTS_CALC_ERROR, "error");
                setRequiredCredits(null);
            } finally {
                if (alive) setLoadingCosts(false);
            }
        })();

        return () => {
            alive = false;
        };
    }, [count, open, result, username, password, showSnackbar]);

    const handleCountChange = (value) => {
        const numeric = Number(value);
        if (Number.isNaN(numeric)) return;

        const clamped = Math.max(1, Math.min(numeric, maxAvailable));
        setCount(clamped);
    };

    const startExport = async () => {
        try {
            const csv = await api.exportResult(username, password, result.id, count);
            downloadTextAsFile(csv, `${result.name}.csv`, "text/csv;charset=utf-8;");
            showSnackbar(UI.MESSAGES.EXPORT_OK, "success");
            onClose();
        } catch {
            showSnackbar(UI.MESSAGES.EXPORT_ERROR, "error");
        }
    };

    if (!result) return null;

    return (
        <ResponsiveDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{UI.DIALOGS.EXPORT_TITLE}</DialogTitle>

            <DialogContent dividers>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <Paper variant="outlined" sx={{ borderRadius: UI.LAYOUT.CARD_RADIUS, p: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                            {result.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                            {UI.LABELS.EXPORT_AVAILABLE(maxAvailable)}
                        </Typography>

                        <Divider sx={{ my: 1.5 }} />

                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
                            <Box>
                                <Typography variant="subtitle2">{UI.LABELS.EXPORT_REQUIRED_CREDITS}</Typography>
                                <Typography
                                    sx={{
                                        fontWeight: 800,
                                        fontSize: 18,
                                        color:
                                            requiredCredits == null
                                                ? "text.secondary"
                                                : creditsEnough
                                                    ? "success.main"
                                                    : "error.main",
                                    }}
                                >
                                    {loadingCosts ? "…" : requiredCredits ?? "-"}
                                </Typography>
                            </Box>

                            {!creditsEnough && requiredCredits != null ? (
                                <Button variant="contained" onClick={onBuyCredits} sx={PREMIUM_SECONDARY_ACTION_SX}>
                                    {UI.BUTTONS.BUY_PACKAGES}
                                </Button>
                            ) : null}
                        </Box>

                        {!creditsEnough && requiredCredits != null ? (
                            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                                {UI.LABELS.EXPORT_NOT_ENOUGH_CREDITS}
                            </Typography>
                        ) : null}
                    </Paper>

                    <TextField
                        type="number"
                        label={UI.LABELS.EXPORT_COUNT}
                        value={count}
                        variant="standard"
                        onChange={(e) => handleCountChange(e.target.value)}
                        inputProps={{ min: 1, max: maxAvailable }}
                        helperText={UI.LABELS.EXPORT_MAX(maxAvailable)}
                        fullWidth
                    />
                </Box>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>{UI.BUTTONS.CANCEL}</Button>
                <Button variant="contained" sx={PREMIUM_SECONDARY_ACTION_SX} disabled={!creditsEnough || loadingCosts || count < 1 || count > maxAvailable} onClick={startExport}>
                    {UI.BUTTONS.DOWNLOAD_START}
                </Button>
            </DialogActions>
        </ResponsiveDialog>
    );
}

/* =========================
   AudienceCreationDialog
   (INTENTIONALLY NOT REWORKED - per request)
========================= */

const LABEL_NAME = "Name";
const LABEL_WEB = "Web";
const LABEL_AGENCY_PARTNER = "Finanzdienstleister";
const LABEL_ZIP_CODE = "PLZ";
const LABEL_ZIP_CODE_START = "PLZ von";
const LABEL_ZIP_CODE_END = "PLZ bis";
const LABEL_RADIUS = "Umkreis";

const DIALOG_CHOOSE_NAME = "Vergeben Sie einen Namen für Ihre Suche.";
const DIALOG_CHOOSE_SOURCE = "Wählen Sie eine Datenquelle.";
const DIALOG_LIMIT_GEO_SCOPE = "Legen Sie den geografischen Suchraum fest.";

const tag_name_label_dict = { tag: "Branche", legal_form: "Rechtsform", financial_service_agency: "Finanzdienstleister" };

function AudienceCreationDialog({ open, handleClose, username, password, showSnackbar, onCreated }) {
    const [activeStep, setActiveStep] = React.useState(0);

    const [audienceName, setAudienceName] = React.useState(null);
    const [webSearch, setWebSearch] = React.useState(true);
    const [radiusSearch, setRadiusSearch] = React.useState(true);

    const [radius, setRadius] = React.useState(25);
    const [zipCode, setZipCode] = React.useState(null);
    const [maxZipCode, setMaxZipCode] = React.useState(null);

    const [isAudienceNameValid, setAudienceNameValid] = React.useState(false);
    const [isMinZipCodeValid, setMinZipCodeValid] = React.useState(false);
    const [isMaxZipCodeValid, setMaxZipCodeValid] = React.useState(false);

    const [descriptor, setDescriptor] = React.useState([]);
    const [paramterValueDict, setParamterValueDict] = React.useState({});

    const [ageRange, setAgeRange] = useState([25, 65]);
    const [isAgeFilterActive, setIsAgeFilterActive] = useState(false);

    const [blockFinancialServiceAgencies, setBlockFinancialServiceAgencies] = useState(false);
    const financialServiceAgencyInfixList = [
        "lvmversicherung",
        "hansemerkurversicherung",
        "interversicherung",
        "allianzversicherung",
        "wgvversicherung",
        "mecklenburgischeversicherung",
        "nürnbergerversicherung",
        "horbachversicherungenundfinanzen",
        "dvagvermögensberatung",
        "continentaleversicherung",
        "zürichversicherung",
        "sdkversicherung",
        "baloiseversicherung",
        "telisversicherung",
        "mannheimerversicherung",
        "compexxfinanz",
        "versicherungskammerbayern",
        "sparkassenversicherung",
        "vpvversicherung",
        "ovbfinanzberatung",
        "finum.privatefinance",
        "hdiversicherung",
        "helvetiaversicherung",
        "signalidunaversicherung",
        "plansecurfinanzpalner",
        "itzehoerversicherung",
        "tecisfinanzberatung",
        "ergoversicherung",
        "diebayerischeversicherung",
        "barmeniaversicherung",
        "saarlandversicherung",
        "hukversicherung",
        "devkversicherung",
        "württembergischeversicherung",
        "axaversicherung",
        "proventusfinanzberatung",
        "bonnfinanzfinanzberatung"
    ];

    const [mandatoryFields, setMandatoryFields] = useState({
        phone: false,
        mobilePhone: false,
        email: true,
        ownerName: false,
        website: false,
        vatId: false,
    });

    const handleToggle = (field) => (event) => {
        setMandatoryFields({ ...mandatoryFields, [field]: event.target.checked });
    };

    const fields = [
        { id: "phone", label: "Telefonnummer" },
        { id: "mobilePhone", label: "Handynummer" },
        { id: "email", label: "E-Mail Adresse" },
        { id: "ownerName", label: "Inhaber / Geschäftsführung" },
        { id: "website", label: "Website URL" },
        { id: "vatId", label: "USt-IdNr. (VAT ID)" },
    ];

    const getSourceName = () => {
        return webSearch ? "Web" : "Financial Service Agencies";
    };

    const isZipCodePrefix = (prefix) => {
        const zipCodePrefixRegex = /^\d*$/;
        return prefix != null && zipCodePrefixRegex.test(prefix);
    };

    const isZipCode = (zipCode) => {
        const zipCodePrefixRegex = /^\d{5}$/;
        return zipCode != null && zipCodePrefixRegex.test(zipCode);
    };

    const onAudienceNameChanged = (name) => {
        const regex = /^[A-Za-zÄÖÜäöü0-9_. ]+$/;

        setAudienceNameValid(regex.test(name));
        setAudienceName(name);
    };

    const onZipCodeChanged = (zipCode) => {
        if (isZipCodePrefix(zipCode)) {
            setZipCode(zipCode);
            setMinZipCodeValid(isZipCode(zipCode));
        }
    };

    const onMaxZipCodeChanged = (newZipCode) => {
        if (isZipCodePrefix(newZipCode)) {
            setMaxZipCode(newZipCode);
            let isMaxZipCodeValid = false;
            if (isZipCode(zipCode) && isZipCode(newZipCode)) {
                const zipCodeValue = parseInt(zipCode, 10);
                const maxZipCodeValue = parseInt(newZipCode, 10);
                isMaxZipCodeValid = zipCodeValue <= maxZipCodeValue;
            }
            setMaxZipCodeValid(isMaxZipCodeValid);
        }
    };

    const getParamterValue = (paramName) => {
        return paramterValueDict[paramName] || [];
    };

    const setParamterValue = (name, value) => {
        const paramterValueDictCpy = { ...paramterValueDict };
        paramterValueDictCpy[name] = value;
        setParamterValueDict(paramterValueDictCpy);
    };

    const clear = () => {
        handleClose();
        setActiveStep(0);
        setAudienceName(null);
        setWebSearch(true);
        setRadiusSearch(true);
        setRadius(5);
        setZipCode(null);
        setMaxZipCode(null);
        setAudienceNameValid(false);
        setMinZipCodeValid(false);
        setMaxZipCodeValid(false);
        setParamterValueDict({});
    };

    const onClose = () => {
        clear();
    };

    const handleToggleChange = (event) => {
        setIsAgeFilterActive(event.target.checked);
    };

    const handleAgeChange = (event, newValue) => {
        if (newValue[0] < newValue[1]) setAgeRange(newValue);
    };

    const onSave = async () => {
        try {
            const createArgumentMap = () => {
                const argumentMap = {};
                const keys = Object.keys(paramterValueDict);
                for (let i = 0; i < keys.length; i++) {
                    argumentMap[keys[i]] = paramterValueDict[keys[i]];
                }
                return argumentMap;
            };

            const sourceName = webSearch ? "Web" : "Financial Service Agencies";

            const baseParams = {
                emailAddress: username,
                password: password,
                name: audienceName,
                ageConstraint: isAgeFilterActive,
                minAge: ageRange[0],
                maxAge: ageRange[1],
                phoneNoRequired: mandatoryFields.phone,
                mobilePhoneNoRequired: mandatoryFields.mobilePhone,
                emailAddressRequired: mandatoryFields.email,
                ownerDirectorRequired: mandatoryFields.ownerName,
                websiteUrlRequired: mandatoryFields.website,
                vatIdRequired: mandatoryFields.vatId,
            };

            let url;
            let params;
            let backend = await backendPool.pickAcceptingBackend();

            if (radiusSearch) {
                params = new URLSearchParams({
                    ...baseParams,
                    zipCode: zipCode,
                    radius: radius,
                });

                url = `${endpoints.createRadialAudience(backend.url, sourceName)}?${params.toString()}`;
            } else {
                params = new URLSearchParams({
                    ...baseParams,
                    minZipCode: zipCode,
                    maxZipCode: maxZipCode,
                });

                url = `${endpoints.createRangeAudience(backend.url, sourceName)}?${params.toString()}`;
            }

            const task = await api.createAudience(url, createArgumentMap());

            showSnackbar(UI.MESSAGES.AUDIENCE_CREATE_OK, "success");

            onCreated?.(task);
            onClose();
        } catch (error) {
            console.error("Audience creation error:", error);
            showSnackbar(UI.MESSAGES.AUDIENCE_CREATE_ERROR, "error");
        }
    };

    const validate = () => {
        if (descriptor == null) {
            return false;
        } else {
            for (let index = 0; index < descriptor.length; index++) {
                if (!validateParamter(descriptor[index])) {
                    return false;
                }
            }
            return true;
        }
    };

    const isEmpty = (value) => {
        if (value === null || value === undefined) return true;
        if (Array.isArray(value)) return value.length === 0;
        if (typeof value === "string") return value.trim() === "";
        return false;
    };

    const validateParamter = (paramDefinition) => {
        const value = paramterValueDict[paramDefinition.name];
        const isOptional = paramDefinition.optional;

        if (isEmpty(value)) {
            return isOptional;
        }

        const valuesToValidate = Array.isArray(value) ? value : [value];

        return valuesToValidate.every((val) => {
            if (paramDefinition.defaultValues?.length > 0) {
                return paramDefinition.defaultValues.includes(val);
            }
            if (paramDefinition.javascriptRegex) {
                return new RegExp(paramDefinition.javascriptRegex).test(val);
            }
            return true;
        });
    };

    const completed = () => {
        let done = true;
        if (activeStep === 0) {
            done = isAudienceNameValid;
        } else if (activeStep === 2) {
            done = validate();
        } else if (activeStep === 4) {
            done = (radiusSearch && isMinZipCodeValid) || (!radiusSearch && isMinZipCodeValid && isMaxZipCodeValid);
        }
        return done;
    };

    const doLoadParamterList = async () => {
        try {
            const data = await api.fetchDescriptor(username, password, getSourceName());
            const filtered = data.filter((paramDefinition) => paramDefinition.name !== "zip_code");

            setDescriptor(filtered);
        } catch (error) {
            console.error("Descriptor loading error:", error);
            showSnackbar(UI.MESSAGES.DESCRIPTOR_LOAD_ERROR, "error");
            setDescriptor([]);
        }
    };

    React.useEffect(() => {
        if (open) {
            doLoadParamterList();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [webSearch, open]);



    useEffect(() => {
        if (!open) return;
        (async () => {
            try {
                setBlockFinancialServiceAgencies((await api.fetchProfile(username, password)).blockFinancialServiceAgencies)
            } catch {
                showSnackbar(UI.MESSAGES.PROFILE_LOAD_ERROR, "error");
            }
        })();
    }, [open, username, password, showSnackbar]);

    const MAX_VALUES = 25;

    const distanceList = [25, 50, 100, 150, 200, 300];

    const steps = [
        {
            count: 1,
            title: UI.BUTTONS.NEW_SEARCH,
            label: LABEL_NAME,
            content: (
                <Grid spacing={5} container>
                    <Grid item xs={12}>
                        <Typography>{DIALOG_CHOOSE_NAME}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label={LABEL_NAME}
                            value={nullToEmpty(audienceName)}
                            onChange={(e) => onAudienceNameChanged(e.target.value)}
                            error={!isAudienceNameValid}
                            variant="standard"
                            fullWidth
                        />
                    </Grid>
                </Grid>
            ),
        },
        {
            count: 2,
            title: UI.BUTTONS.NEW_SEARCH,
            label: "Quelle",
            content: (
                <Grid spacing={3} container>
                    <Grid xs={12} item>
                        <Typography>{DIALOG_CHOOSE_SOURCE}</Typography>
                    </Grid>
                    <Grid xs={12} item>
                        <Grid xs={12}>
                            <Button
                                variant="text"
                                color="primary"
                                style={{ color: webSearch ? UI.BRAND.PRIMARY_COLOR : "gray" }}
                                startIcon={<LanguageIcon style={{ color: webSearch ? UI.BRAND.PRIMARY_COLOR : "gray" }} />}
                                onClick={() => setWebSearch(true)}
                            >
                                {LABEL_WEB}
                            </Button>
                        </Grid>
                        {
                            blockFinancialServiceAgencies ?
                                null
                                :
                                <Grid xs={12}>
                                    <Button
                                        variant="text"
                                        color="primary"
                                        style={{ color: !webSearch ? UI.BRAND.PRIMARY_COLOR : "gray" }}
                                        startIcon={<WorkOutlineIcon style={{ color: !webSearch ? UI.BRAND.PRIMARY_COLOR : "gray" }} />}
                                        onClick={() => setWebSearch(false)}
                                    >
                                        {LABEL_AGENCY_PARTNER}
                                    </Button>
                                </Grid>
                        }
                    </Grid>
                </Grid>
            ),
        },
        {
            count: 3,
            title: UI.BUTTONS.NEW_SEARCH,
            label: "Branche und Rechtsform",
            content: (
                <Box sx={{ p: 2 }}>
                    <Grid container spacing={3}>
                        {descriptor.map((paramDefinition) => {
                            const isValid = validateParamter(paramDefinition);
                            const paramName = paramDefinition.name;
                            const currentValues = getParamterValue(paramName)
                            const paramDefinitionDefaultValues = blockFinancialServiceAgencies ?
                                paramDefinition.defaultValues.filter(value => {
                                    const normalized = value.replace(/\s+/g, '').toLowerCase();
                                    return !financialServiceAgencyInfixList.includes(normalized);
                                })
                                :
                                paramDefinition.defaultValues
                            return (
                                <Grid item key={tag_name_label_dict[paramName] ?? paramName} xs={12}>
                                    <Paper
                                        variant="outlined"
                                        sx={{
                                            borderRadius: 2,
                                            p: 3,
                                            transition: "0.3s",
                                            borderLeft: `6px solid ${isValid ? UI.BRAND.PRIMARY_COLOR : "#d32f2f"}`,
                                        }}
                                    >
                                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                                            <Typography variant="subtitle2" color="textSecondary" sx={{ fontWeight: "bold" }}>
                                                {tag_name_label_dict[paramName] ?? paramName}
                                            </Typography>
                                        </Box>

                                        <Autocomplete
                                            multiple={paramDefinition.multiValueParameter}
                                            id={`tags-${paramName}`}
                                            disabled={currentValues.length >= MAX_VALUES}
                                            options={paramDefinitionDefaultValues || []}
                                            freeSolo={paramDefinitionDefaultValues == null || paramDefinitionDefaultValues.length === 0}
                                            value={currentValues}
                                            onChange={(event, newValue) => {
                                                setParamterValue(paramName, newValue || []);
                                            }}
                                            renderTags={(tagValue, getTagProps) =>
                                                tagValue.map((option, index) => (
                                                    <Chip label={option} {...getTagProps({ index })} color="primary" variant="outlined" size="small" />
                                                ))
                                            }
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    variant="outlined"
                                                    label={`${tag_name_label_dict[paramName] ?? paramName} auswählen oder tippen`}
                                                    placeholder="Hinzufügen..."
                                                    error={!isValid}
                                                    helperText={!isValid ? "Dieses Feld benötigt mindestens einen Wert" : ""}
                                                />
                                            )}
                                        />
                                    </Paper>
                                </Grid>
                            );
                        })}
                    </Grid>
                </Box>
            ),
        },
        {
            count: 5,
            title: UI.BUTTONS.NEW_SEARCH,
            label: "Filter",
            content: (
                <Grid container spacing={6}>
                    <Grid item xs={12}>
                        <Box sx={{ border: "1px solid #e0e0e0", borderRadius: 2, p: 2, bgcolor: isAgeFilterActive ? "transparent" : "#f9f9f9" }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <FormControlLabel
                                        control={<Switch checked={isAgeFilterActive} onChange={handleToggleChange} color="primary" />}
                                        label={<Typography variant="subtitle2">Altersspanne für Geschäftsführung festlegen</Typography>}
                                    />
                                    <Tooltip title={UI.TOOLTIPS.AGE_FILTER_INFO} arrow>
                                        <IconButton size="small">
                                            <InfoIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </Box>

                            <Box sx={{ px: 2, opacity: isAgeFilterActive ? 1 : 0.5 }}>
                                <Typography id="age-range-slider" gutterBottom variant="caption" color="textSecondary">
                                    Gewählter Bereich: {ageRange[0]} - {ageRange[1]} Jahre
                                </Typography>
                                <Slider
                                    disabled={!isAgeFilterActive}
                                    value={ageRange}
                                    onChange={handleAgeChange}
                                    valueLabelDisplay="auto"
                                    min={18}
                                    max={85}
                                    aria-labelledby="age-range-slider"
                                />
                                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                                    <Typography variant="caption" color={isAgeFilterActive ? "textPrimary" : "disabled"}>
                                        18 J.
                                    </Typography>
                                    <Typography variant="caption" color={isAgeFilterActive ? "textPrimary" : "disabled"}>
                                        85 J.
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Grid>

                    <Grid item xs={12}>
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: "#fcfcfc" }}>
                            <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 2 }}>
                                Wählen Sie aus, welche Informationen ein gültiger Datensatz zwingend enthalten muss.
                            </Typography>

                            <Divider sx={{ mb: 2 }} />

                            <Grid container spacing={1}>
                                {fields.map((field) => (
                                    <Grid item xs={12} sm={6} key={field.id}>
                                        <Box sx={{ display: "flex", alignItems: "center", p: 1, borderRadius: 1, "&:hover": { bgcolor: "#f0f4f8" } }}>
                                            <FormControlLabel
                                                control={<Switch checked={mandatoryFields[field.id]} onChange={handleToggle(field.id)} size="small" />}
                                                label={<Typography variant="body2">{field.label}</Typography>}
                                                sx={{ width: "100%", m: 0 }}
                                            />
                                        </Box>
                                    </Grid>
                                ))}
                            </Grid>
                        </Paper>
                    </Grid>
                </Grid>
            ),
        },
        {
            count: 4,
            title: UI.BUTTONS.NEW_SEARCH,
            label: "Suchraum",
            content: (
                <Grid container spacing={6}>
                    <Grid item xs={12}>
                        <Typography>{DIALOG_LIMIT_GEO_SCOPE}</Typography>
                    </Grid>

                    <Grid item xs={12} container justifyContent="center">
                        <Button
                            size="large"
                            variant="text"
                            color="primary"
                            startIcon={<MapIcon style={{ color: !radiusSearch ? UI.BRAND.PRIMARY_COLOR : "gray" }} />}
                            style={{ color: !radiusSearch ? UI.BRAND.PRIMARY_COLOR : "gray" }}
                            onClick={() => setRadiusSearch(false)}
                        >
                            {LABEL_ZIP_CODE}
                        </Button>
                        <Button
                            size="large"
                            variant="text"
                            color="primary"
                            startIcon={<TrackChangesIcon style={{ color: radiusSearch ? UI.BRAND.PRIMARY_COLOR : "gray" }} />}
                            style={{ color: radiusSearch ? UI.BRAND.PRIMARY_COLOR : "gray" }}
                            onClick={() => setRadiusSearch(true)}
                        >
                            {LABEL_RADIUS}
                        </Button>
                    </Grid>

                    <Grid item xs={6}>
                        <TextField
                            variant="standard"
                            label={radiusSearch ? LABEL_ZIP_CODE : LABEL_ZIP_CODE_START}
                            fullWidth
                            error={!isMinZipCodeValid}
                            value={nullToEmpty(zipCode)}
                            onChange={(e) => onZipCodeChanged(e.target.value)}
                        />
                    </Grid>

                    {radiusSearch ? (
                        <Grid item xs={6}>
                            <FormControl fullWidth>
                                <InputLabel>{LABEL_RADIUS}</InputLabel>
                                <Select label={LABEL_RADIUS} variant="standard" value={radius} onChange={(e) => setRadius(e.target.value)}>
                                    {distanceList.map((distance) => (
                                        <MenuItem key={distance} value={distance}>
                                            {distance} km
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    ) : isMinZipCodeValid ? (
                        <Grid item xs={6}>
                            <TextField
                                variant="standard"
                                label={LABEL_ZIP_CODE_END}
                                error={!isMaxZipCodeValid}
                                fullWidth
                                value={nullToEmpty(maxZipCode)}
                                onChange={(e) => onMaxZipCodeChanged(e.target.value)}
                            />
                        </Grid>
                    ) : null}
                </Grid>
            ),
        },
    ];

    const isLastStep = activeStep === steps.length - 1;

    return (
        <ResponsiveDialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            sx={{
                "& .MuiButton-root": {
                    minHeight: "auto",
                    lineHeight: 1.75,
                    whiteSpace: "normal",
                    boxShadow: "none",
                },
                "& .MuiButton-sizeMedium": {
                    px: 1,
                    py: 0.75,
                },
                "& .MuiButton-sizeLarge": {
                    px: 1.375,
                    py: 1,
                },
            }}
        >
            <DialogTitle style={{ backgroundColor: UI.BRAND.PRIMARY_COLOR, color: "white" }}>{steps[activeStep].title}</DialogTitle>

            <DialogContent dividers sx={{ px: { xs: 1.5, sm: 3 } }}>
                <Box sx={{ p: { xs: 2, sm: 6.25 }, maxWidth: 800, mx: "auto" }}>{steps[activeStep].content}</Box>
            </DialogContent>

            <DialogActions>
                <Button variant="text" color="primary" onClick={onClose}>{UI.BUTTONS.CANCEL}</Button>
                <Button variant="text" color="primary" onClick={() => setActiveStep((prev) => prev - 1)} disabled={activeStep === 0}>
                    {UI.BUTTONS.PREV}
                </Button>
                <Button variant="text" color="primary" onClick={isLastStep ? onSave : () => setActiveStep((prev) => prev + 1)} disabled={!completed()}>
                    {isLastStep ? UI.BUTTONS.FINISH : UI.BUTTONS.NEXT}
                </Button>
            </DialogActions>
        </ResponsiveDialog>
    );
}

/* =========================
   PRODUCT-LIKE LIST CARDS
========================= */

const REGISTER_STYLE = Object.freeze({
    surface: {
        width: "100%",
        overflow: "hidden",
        borderRadius: 3,
        borderColor: "rgba(12, 5, 109, 0.14)",
        boxShadow: "0 12px 30px rgba(12, 5, 109, 0.06)",
    },
    columnHeader: {
        display: { xs: "none", md: "grid" },
        alignItems: "center",
        gap: 2,
        px: 3,
        py: 1.5,
        bgcolor: "rgba(12,5,109,.035)",
        borderBottom: "1px solid rgba(12,5,109,.09)",
    },
    row: {
        px: { xs: 2, sm: 3 },
        py: { xs: 2, sm: 2.5 },
        transition: "background-color .15s ease",
        "&:hover": { bgcolor: "rgba(12,5,109,.022)" },
    },
    label: {
        display: { xs: "block", md: "none" },
        mb: 0.45,
        color: "text.secondary",
    },
});

function StatusPill({ children, muted = false, error = false }) {
    return (
        <Box
            component="span"
            sx={{
                display: "inline-flex",
                alignItems: "center",
                width: "fit-content",
                px: 1.15,
                py: 0.5,
                borderRadius: "999px",
                border: "1px solid",
                borderColor: error ? "rgba(211,47,47,.26)" : muted ? "rgba(0,0,0,.14)" : "rgba(12,5,109,.22)",
                bgcolor: error ? "rgba(211,47,47,.055)" : muted ? "rgba(0,0,0,.035)" : "rgba(12,5,109,.055)",
                color: error ? "error.main" : muted ? "text.secondary" : "primary.main",
                fontSize: "0.75rem",
                lineHeight: 1.35,
                whiteSpace: "nowrap",
            }}
        >
            {children}
        </Box>
    );
}


const REGISTER_PAGE_SIZE = 5;

function RegisterPagination({ page, pageCount, total, pageSize = REGISTER_PAGE_SIZE, onChange }) {
    if (pageCount <= 1) return null;

    const first = (page - 1) * pageSize + 1;
    const last = Math.min(page * pageSize, total);

    return (
        <Box
            sx={{
                px: { xs: 1.75, sm: 3 },
                py: { xs: 1, sm: 1.5 },
                borderTop: "1px solid rgba(12,5,109,.09)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                flexWrap: "wrap",
                bgcolor: "rgba(12,5,109,.018)",
            }}
        >
            <Typography variant="caption" color="text.secondary">
                {first}–{last} von {total}
            </Typography>
            <Box sx={{ display: { xs: "flex", sm: "none" }, alignItems: "center", gap: 0.75 }}>
                <IconButton
                    size="small"
                    aria-label="Vorherige Seite"
                    disabled={page <= 1}
                    onClick={() => onChange(Math.max(1, page - 1))}
                    sx={{ width: 40, height: 40, color: "primary.main" }}
                >
                    <ChevronLeftIcon />
                </IconButton>
                <Typography variant="caption" color="text.secondary" sx={{ minWidth: 48, textAlign: "center", fontVariantNumeric: "tabular-nums" }}>
                    {page} / {pageCount}
                </Typography>
                <IconButton
                    size="small"
                    aria-label="Nächste Seite"
                    disabled={page >= pageCount}
                    onClick={() => onChange(Math.min(pageCount, page + 1))}
                    sx={{ width: 40, height: 40, color: "primary.main" }}
                >
                    <ChevronRightIcon />
                </IconButton>
            </Box>
            <Box sx={{ display: { xs: "none", sm: "block" } }}>
                <Pagination
                    page={page}
                    count={pageCount}
                    onChange={(_, nextPage) => onChange(nextPage)}
                    size="small"
                    color="primary"
                    showFirstButton
                    showLastButton
                />
            </Box>
        </Box>
    );
}

function RegisterSearchField({ value, onChange, placeholder = "Suchen" }) {
    return (
        <TextField
            value={value}
            onChange={(event) => onChange(event.target.value)}
            placeholder={placeholder}
            size="small"
            variant="outlined"
            inputProps={{ "aria-label": placeholder }}
            InputProps={{
                startAdornment: (
                    <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                    </InputAdornment>
                ),
                endAdornment: value ? (
                    <InputAdornment position="end">
                        <IconButton
                            size="small"
                            aria-label="Suche leeren"
                            onClick={() => onChange("")}
                            sx={{ width: 26, height: 26, color: "text.secondary" }}
                        >
                            <CloseIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                    </InputAdornment>
                ) : null,
            }}
            sx={{
                width: { xs: "100%", sm: 290 },
                "& .MuiOutlinedInput-root": {
                    bgcolor: "rgba(255,255,255,.92)",
                    borderRadius: 2,
                },
                "& .MuiOutlinedInput-input": {
                    py: 1.05,
                },
            }}
        />
    );
}

function RegisterHeaderSearch({ value, onChange, placeholder }) {
    return (
        <Box
            sx={{
                px: { xs: 1.75, sm: 3 },
                pt: 1.35,
                pb: 0.5,
                display: "flex",
                justifyContent: "flex-end",
                bgcolor: "rgba(12,5,109,.035)",
            }}
        >
            <RegisterSearchField value={value} onChange={onChange} placeholder={placeholder} />
        </Box>
    );
}

function ObjectSurfaceHeader({ title, badges = [] }) {
    return (
        <Box
            sx={{
                p: "20px",
                bgcolor: "primary.main",
                color: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
            }}
        >
            <Typography component="h2" sx={{ minWidth: 0, lineHeight: 1.25, overflowWrap: "anywhere" }}>
                {title || "–"}
            </Typography>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end" }}>
                {badges.filter(Boolean).map((badge, index) => (
                    <Box
                        component="span"
                        key={`${badge}-${index}`}
                        sx={{
                            px: "10px",
                            py: "5px",
                            borderRadius: "999px",
                            border: "1px solid rgba(255,255,255,.38)",
                            bgcolor: "rgba(255,255,255,.18)",
                            color: "white",
                            fontSize: "0.75rem",
                            lineHeight: 1.4,
                            whiteSpace: "nowrap",
                        }}
                    >
                        {badge}
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

const getActiveSubtask = (task) => {
    if (!task?.subtaskList) return null;
    return task.subtaskList.find((st) => st.status === UI.STATUS.TASK.RUNNING);
};

function TaskCard({ task, onOpenMenu, position, total }) {
    const activeSubtask = getActiveSubtask(task);
    const running = task.status === UI.STATUS.TASK.RUNNING;
    const statusMeta = getTaskStatusChipMeta(task.status);
    const overall = getOverallTaskProgress(task);

    const statusText = running
        ? (activeSubtask ? SUBTASK_NAME_TRANSLATION_DICT[activeSubtask.name] ?? UI.STATUS.LABELS.RUNNING : UI.STATUS.LABELS.RUNNING)
        : task.status === UI.STATUS.TASK.SCHEDULED
            ? `${UI.STATUS.LABELS.SCHEDULED}${task.position != null ? ` · Position ${task.position}` : ""}`
            : task.status === UI.STATUS.TASK.FAILED
                ? UI.STATUS.LABELS.FAILED
                : UI.STATUS.LABELS.COMPLETED;

    return (
        <Paper variant="outlined" sx={REGISTER_STYLE.surface}>
            <ObjectSurfaceHeader
                title={(
                    <Box component="span" sx={{ display: "inline", overflowWrap: "anywhere" }}>
                        {task.name || "–"}
                        {position && total ? (
                            <Box component="span" sx={{ ml: 1, opacity: 0.68, fontWeight: 400, fontSize: "0.86em" }}>
                                ({position} von {total})
                            </Box>
                        ) : null}
                    </Box>
                )}
                badges={[statusMeta.label, running ? "Research" : null]}
            />

            <Box sx={{ p: "25px" }}>
                {running ? (
                    <ResearchProgressVisualization task={task} />
                ) : (
                    <Grid container spacing={3} alignItems="center">
                        <Grid item xs={12} md={6}>
                            <Typography variant="body2" color="text.secondary">
                                Research-Status
                            </Typography>
                            <Typography variant="h6" sx={{ mt: 0.45, overflowWrap: "anywhere" }}>
                                {statusText}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="body2" color="text.secondary">
                                Fortschritt
                            </Typography>
                            <Typography variant="body1" sx={{ mt: 0.45 }}>
                                {overall}%
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="body2" color="text.secondary">
                                Auftraggeber
                            </Typography>
                            <Typography variant="body1" sx={{ mt: 0.45, overflowWrap: "anywhere" }}>
                                {task?.ownerDTO?.emailAddress || "–"}
                            </Typography>
                        </Grid>
                    </Grid>
                )}
            </Box>

            {(running || task.status === UI.STATUS.TASK.SCHEDULED) ? (
                <Box sx={{ px: "25px", pb: "18px", display: "flex", justifyContent: "flex-end" }}>
                    <Tooltip title={UI.TOOLTIPS.TASK_MENU} arrow>
                        <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); onOpenMenu(e, task); }}
                            aria-label={UI.ARIA.ACTIONS}
                        >
                            <MoreVertIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            ) : null}
        </Paper>
    );
}


const taskKey = (task) => `${task?.id ?? "task"}-${task?.backendId ?? "backend"}`;

function ResearchCompanionBar({ paused, onTogglePause, onOpenOverview, taskCount, activeIndex }) {
    const safeCount = Math.max(1, Number(taskCount) || 1);
    const safeIndex = Math.max(0, Math.min(safeCount - 1, Number(activeIndex) || 0));

    return (
        <Paper
            variant="outlined"
            sx={{
                mt: 2.25,
                px: { xs: 1.5, sm: 2.25 },
                py: 1.25,
                borderRadius: 3,
                borderColor: "rgba(12,5,109,.14)",
                boxShadow: "0 8px 24px rgba(12,5,109,.045)",
                bgcolor: "rgba(255,255,255,.98)",
            }}
        >
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "minmax(0,1fr) auto" },
                    gap: { xs: 1.25, sm: 2 },
                    alignItems: "center",
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.4, minWidth: 0 }}>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ whiteSpace: "nowrap", minWidth: 34, fontVariantNumeric: "tabular-nums" }}
                    >
                        {safeIndex + 1} / {safeCount}
                    </Typography>
                    <Box
                        aria-label={`Suchvorgang ${safeIndex + 1} von ${safeCount}`}
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.45,
                            flex: 1,
                            minWidth: 0,
                            maxWidth: 340,
                            height: 12,
                        }}
                    >
                        {Array.from({ length: safeCount }).map((_, index) => {
                            const active = index === safeIndex;
                            return (
                                <Box
                                    key={index}
                                    sx={{
                                        flex: "1 1 0",
                                        minWidth: 3,
                                        maxWidth: 18,
                                        height: active ? 7 : 4,
                                        borderRadius: "999px",
                                        bgcolor: active ? "secondary.main" : "rgba(12,5,109,.14)",
                                        boxShadow: active ? "0 0 0 2px rgba(83,64,255,.08)" : "none",
                                        opacity: active ? 1 : 0.9,
                                        transition: "height .24s ease, background-color .24s ease, box-shadow .24s ease",
                                    }}
                                />
                            );
                        })}
                    </Box>
                </Box>

                <Box sx={{ display: "flex", justifyContent: { xs: "flex-end", sm: "flex-start" }, gap: 1, flexWrap: "wrap" }}>
                    <Tooltip title={paused ? "Automatischen Wechsel fortsetzen" : "Automatischen Wechsel pausieren"} arrow>
                        <Button
                            size="small"
                            variant="text"
                            startIcon={paused ? <PlayArrowIcon /> : <PauseIcon />}
                            onClick={onTogglePause}
                            sx={{
                                color: "secondary.main",
                                bgcolor: "transparent",
                                border: "none",
                                boxShadow: "none",
                                px: 1.5,
                                "&:hover": {
                                    bgcolor: "rgba(83,64,255,.06)",
                                    boxShadow: "none",
                                },
                            }}
                        >
                            {paused ? "Fortsetzen" : "Pausieren"}
                        </Button>
                    </Tooltip>

                    <Tooltip title="Alle laufenden Research-Vorgänge anzeigen" arrow>
                        <Button
                            size="small"
                            variant="text"
                            startIcon={<OpenInFullIcon />}
                            onClick={onOpenOverview}
                            sx={{
                                color: "secondary.main",
                                bgcolor: "transparent",
                                border: "none",
                                boxShadow: "none",
                                px: 1.5,
                                "&:hover": {
                                    bgcolor: "rgba(83,64,255,.06)",
                                    boxShadow: "none",
                                },
                            }}
                        >
                            Übersicht
                        </Button>
                    </Tooltip>
                </Box>
            </Box>
        </Paper>
    );
}

function ResearchOverviewDialog({ open, onClose, tasks, onOpenTask }) {
    const ongoingTasks = useMemo(
        () => tasks.filter((task) => (
            task.status === UI.STATUS.TASK.RUNNING
            || task.status === UI.STATUS.TASK.SCHEDULED
        )),
        [tasks]
    );

    return (
        <ResponsiveDialog fullScreen open={open} onClose={onClose}>
            <AppBar position="relative" sx={{ backgroundColor: UI.BRAND.PRIMARY_COLOR }}>
                <Toolbar>
                    <IconButton
                        edge="start"
                        color="inherit"
                        onClick={onClose}
                        aria-label="Research-Übersicht schließen"
                    >
                        <CloseIcon />
                    </IconButton>
                    <Typography variant="h6" component="div" sx={{ ml: 2, flex: 1 }}>
                        Vorgänge
                    </Typography>
                </Toolbar>
            </AppBar>

            <DialogContent sx={{ backgroundColor: "background.default", p: 0 }}>
                <Box sx={{ m: { xs: 2, sm: 4, md: "75px" } }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 780, lineHeight: 1.6 }}>
                        {ongoingTasks.length === 1
                            ? "Ein Research-Vorgang ist aktuell aktiv oder eingeplant."
                            : `${ongoingTasks.length} Research-Vorgänge sind aktuell aktiv oder eingeplant.`}
                    </Typography>

                    {ongoingTasks.length === 0 ? (
                        <Paper variant="outlined" sx={{ p: 3 }}>
                            <Typography variant="body1">Aktuell laufen keine Research-Vorgänge.</Typography>
                        </Paper>
                    ) : (
                        <Paper
                            variant="outlined"
                            sx={{
                                overflow: "hidden",
                                borderColor: "rgba(12,5,109,.14)",
                                boxShadow: "0 12px 30px rgba(12,5,109,.06)",
                            }}
                        >
                            <Box
                                sx={{
                                    display: { xs: "none", md: "grid" },
                                    gridTemplateColumns: "minmax(0,1.45fr) minmax(220px,1fr) minmax(220px,.9fr) 230px",
                                    gap: 3,
                                    px: 3,
                                    py: 1.5,
                                    bgcolor: "rgba(12,5,109,.025)",
                                    borderBottom: "1px solid rgba(12,5,109,.09)",
                                }}
                            >
                                <Typography variant="caption" color="text.secondary">Suchvorgang</Typography>
                                <Typography variant="caption" color="text.secondary">Aktuelle Phase</Typography>
                                <Typography variant="caption" color="text.secondary">Gesamtfortschritt</Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ textAlign: "right" }}>Aktion</Typography>
                            </Box>

                            {ongoingTasks.map((task, index) => {
                                const activeSubtask = getActiveSubtask(task);
                                const phaseName = activeSubtask
                                    ? (SUBTASK_NAME_TRANSLATION_DICT[activeSubtask.name] ?? activeSubtask.name)
                                    : task.status === UI.STATUS.TASK.SCHEDULED
                                        ? "Recherche eingeplant"
                                        : "Recherche vorbereiten";
                                const overall = getOverallTaskProgress(task);

                                return (
                                    <React.Fragment key={taskKey(task)}>
                                        {index > 0 ? <Divider /> : null}
                                        <Box
                                            sx={{
                                                display: "grid",
                                                gridTemplateColumns: { xs: "1fr", md: "minmax(0,1.45fr) minmax(220px,1fr) minmax(220px,.9fr) 230px" },
                                                gap: { xs: 2, md: 3 },
                                                alignItems: "center",
                                                px: { xs: 2, sm: 3 },
                                                py: { xs: 2, sm: 2.5 },
                                                transition: "background-color .15s ease",
                                                "&:hover": { bgcolor: "rgba(12,5,109,.022)" },
                                            }}
                                        >
                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography variant="body1" sx={{ overflowWrap: "anywhere" }}>
                                                    {task.name || `Suchvorgang ${task.id}`}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.35 }}>
                                                    {task.status === UI.STATUS.TASK.RUNNING ? "In Bearbeitung" : "Geplant"}
                                                </Typography>
                                            </Box>

                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography variant="caption" color="text.secondary" sx={{ display: { xs: "block", md: "none" }, mb: 0.35 }}>
                                                    Aktuelle Phase
                                                </Typography>
                                                <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
                                                    {phaseName}
                                                </Typography>
                                            </Box>

                                            <Box>
                                                <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, mb: 0.7 }}>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Gesamt
                                                    </Typography>
                                                    <Typography variant="body2">{overall}%</Typography>
                                                </Box>
                                                <LinearProgress
                                                    variant="determinate"
                                                    value={overall}
                                                    sx={{
                                                        height: 5,
                                                        bgcolor: "rgba(12,5,109,.08)",
                                                        "& .MuiLinearProgress-bar": { bgcolor: "primary.main" },
                                                    }}
                                                />
                                            </Box>

                                            <Box sx={{ display: "flex", justifyContent: { xs: "flex-start", md: "flex-end" } }}>
                                                <Button
                                                    size="small"
                                                    endIcon={<ArrowForwardIcon />}
                                                    onClick={() => onOpenTask(task)}
                                                    sx={{ minWidth: { xs: 0, sm: 190 }, width: { xs: "100%", sm: "auto" }, justifyContent: "center" }}
                                                >
                                                    Zur Detailansicht
                                                </Button>
                                            </Box>
                                        </Box>
                                    </React.Fragment>
                                );
                            })}
                        </Paper>
                    )}
                </Box>
            </DialogContent>
        </ResponsiveDialog>
    );
}

const getResultTimestamp = (result) => {
    const candidates = [
        result?.createdAt,
        result?.createdTimestamp,
        result?.creationTimestamp,
        result?.timestamp,
        result?.updatedAt,
    ];

    for (const candidate of candidates) {
        if (candidate == null) continue;
        const numeric = Number(candidate);
        if (Number.isFinite(numeric) && numeric > 0) return numeric;
        const parsed = Date.parse(candidate);
        if (Number.isFinite(parsed)) return parsed;
    }
    return null;
};

const compareResultsNewestFirst = (left, right) => {
    const leftTimestamp = getResultTimestamp(left);
    const rightTimestamp = getResultTimestamp(right);
    if (leftTimestamp != null || rightTimestamp != null) {
        return (rightTimestamp ?? -Infinity) - (leftTimestamp ?? -Infinity);
    }

    const leftId = Number(left?.id);
    const rightId = Number(right?.id);
    if (Number.isFinite(leftId) && Number.isFinite(rightId)) return rightId - leftId;

    return String(right?.id ?? "").localeCompare(String(left?.id ?? ""), "de", { numeric: true });
};

function ResultList({ results, onPreview, onExport }) {
    const [page, setPage] = useState(1);
    const [searchQuery, setSearchQuery] = useState("");
    const sortedResults = useMemo(() => [...results].sort(compareResultsNewestFirst), [results]);
    const filteredResults = useMemo(() => {
        const query = searchQuery.trim().toLocaleLowerCase("de-DE");
        if (!query) return sortedResults;
        return sortedResults.filter((result) => {
            const values = [
                result?.name,
                result?.id,
                result?.size,
                result?.exportSize,
                result?.cleared ? "exportbereit" : "",
            ];
            return values.some((value) => String(value ?? "").toLocaleLowerCase("de-DE").includes(query));
        });
    }, [sortedResults, searchQuery]);
    const pageCount = Math.max(1, Math.ceil(filteredResults.length / REGISTER_PAGE_SIZE));
    const visibleResults = filteredResults.slice((page - 1) * REGISTER_PAGE_SIZE, page * REGISTER_PAGE_SIZE);
    const newestResultId = sortedResults[0]?.id ?? null;

    useEffect(() => {
        if (page > pageCount) setPage(pageCount);
    }, [page, pageCount]);

    useEffect(() => {
        setPage(1);
    }, [newestResultId, results.length, searchQuery]);

    return (
        <Paper variant="outlined" sx={REGISTER_STYLE.surface}>
            <RegisterHeaderSearch
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder="Ergebnisse durchsuchen"
            />
            <Box
                sx={{
                    ...REGISTER_STYLE.columnHeader,
                    gridTemplateColumns: "minmax(0,1.8fr) 150px 140px 320px",
                    pt: 1.1,
                }}
            >
                <Typography variant="caption" color="text.secondary">Audience</Typography>
                <Typography variant="caption" color="text.secondary">Datensätze</Typography>
                <Typography variant="caption" color="text.secondary">Status</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ textAlign: "right" }}>Aktionen</Typography>
            </Box>

            {visibleResults.length === 0 ? (
                <Box sx={{ px: 3, py: 5, textAlign: "center" }}>
                    <Typography variant="body2" color="text.secondary">Keine passenden Ergebnisse gefunden.</Typography>
                </Box>
            ) : visibleResults.map((result, index) => {
                const count = result.cleared ? result.exportSize : result.size;
                const countLabel = new Intl.NumberFormat("de-DE").format(count ?? 0);
                const ready = count > 0;

                return (
                    <React.Fragment key={result.id}>
                        {index > 0 ? <Divider /> : null}
                        <Box
                            sx={{
                                ...REGISTER_STYLE.row,
                                display: "grid",
                                gridTemplateColumns: { xs: "1fr", md: "minmax(0,1.8fr) 150px 140px 320px" },
                                gap: { xs: 2, md: 2 },
                                alignItems: { xs: "start", md: "center" },
                            }}
                        >
                            <Box sx={{ minWidth: 0 }}>
                                <Typography variant="caption" sx={REGISTER_STYLE.label}>Audience</Typography>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        lineHeight: 1.4,
                                        whiteSpace: "normal",
                                        overflow: "visible",
                                        textOverflow: "clip",
                                        overflowWrap: "anywhere",
                                    }}
                                >
                                    {result.name || "–"}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.45 }}>
                                    Ergebnis #{result.id}
                                </Typography>
                            </Box>

                            <Box>
                                <Typography variant="caption" sx={REGISTER_STYLE.label}>Datensätze</Typography>
                                <Typography variant="h6">{countLabel}</Typography>
                            </Box>

                            <Box>
                                <Typography variant="caption" sx={REGISTER_STYLE.label}>Status</Typography>
                                <StatusPill muted={!ready}>{ready ? "Exportbereit" : "Leer"}</StatusPill>
                            </Box>

                            <Box sx={{ display: "flex", justifyContent: { xs: "flex-start", md: "flex-end" }, gap: 1.25, flexWrap: { xs: "wrap", md: "nowrap" }, alignItems: "center" }}>
                                <Tooltip title={UI.TOOLTIPS.RESULT_PREVIEW} arrow>
                                    <span>
                                        <Button
                                            variant="contained"
                                            startIcon={<SearchIcon />}
                                            sx={{ minWidth: 130, justifyContent: "center" }}
                                            disabled={result.size === 0}
                                            onClick={() => onPreview(result)}
                                        >
                                            {UI.BUTTONS.PREVIEW}
                                        </Button>
                                    </span>
                                </Tooltip>
                                <Tooltip title={UI.TOOLTIPS.RESULT_EXPORT} arrow>
                                    <span>
                                        <Button
                                            variant="contained"
                                            sx={{ ...PREMIUM_SECONDARY_ACTION_SX, minWidth: 130, justifyContent: "center" }}
                                            startIcon={<DownloadIcon />}
                                            disabled={result.size === 0}
                                            onClick={() => onExport(result)}
                                        >
                                            {UI.BUTTONS.EXPORT}
                                        </Button>
                                    </span>
                                </Tooltip>
                            </Box>
                        </Box>
                    </React.Fragment>
                );
            })}

            <RegisterPagination
                page={page}
                pageCount={pageCount}
                total={filteredResults.length}
                onChange={setPage}
            />
        </Paper>
    );
}

const getOrderStatusLabel = (status) => {
    switch (status) {
        case UI.ORDER_STATUS.OPEN: return "Offen";
        case UI.ORDER_STATUS.PAYMENT_PENDING: return "Zahlung ausstehend";
        case UI.ORDER_STATUS.PAID: return "Bezahlt";
        case UI.ORDER_STATUS.FAILED: return "Fehlgeschlagen";
        case UI.ORDER_STATUS.EXPIRED: return "Abgelaufen";
        case UI.ORDER_STATUS.REFUNDED: return "Rückerstattet";
        case UI.ORDER_STATUS.DISPUTED: return "Angefochten";
        case UI.ORDER_STATUS.DISPUTED_AND_REFUNDED: return "Rückabgewickelt";
        default: return "Unbekannt";
    }
};

function OrderList({ orders, onInvoice }) {
    const [page, setPage] = useState(1);
    const [searchQuery, setSearchQuery] = useState("");
    const filteredOrders = useMemo(() => {
        const query = searchQuery.trim().toLocaleLowerCase("de-DE");
        if (!query) return orders;
        return orders.filter((order) => {
            const values = [
                order?.orderId,
                order?.creditCount,
                getOrderStatusLabel(order?.status),
                formatDate(order?.creationTimestamp),
                order?.invoiced ? "rechnung" : "",
            ];
            return values.some((value) => String(value ?? "").toLocaleLowerCase("de-DE").includes(query));
        });
    }, [orders, searchQuery]);
    const pageCount = Math.max(1, Math.ceil(filteredOrders.length / REGISTER_PAGE_SIZE));
    const visibleOrders = filteredOrders.slice((page - 1) * REGISTER_PAGE_SIZE, page * REGISTER_PAGE_SIZE);

    useEffect(() => {
        if (page > pageCount) setPage(pageCount);
    }, [page, pageCount]);

    useEffect(() => {
        setPage(1);
    }, [searchQuery, orders.length]);

    return (
        <Paper variant="outlined" sx={REGISTER_STYLE.surface}>
            <RegisterHeaderSearch
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder="Bestellungen durchsuchen"
            />
            <Box
                sx={{
                    ...REGISTER_STYLE.columnHeader,
                    gridTemplateColumns: "minmax(180px,1fr) 180px 160px minmax(180px,1fr) 170px",
                    pt: 1.1,
                }}
            >
                <Typography variant="caption" color="text.secondary">Bestellung</Typography>
                <Typography variant="caption" color="text.secondary">Credit-Paket</Typography>
                <Typography variant="caption" color="text.secondary">Erstellt</Typography>
                <Typography variant="caption" color="text.secondary">Status</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ textAlign: "right" }}>Beleg</Typography>
            </Box>

            {visibleOrders.length === 0 ? (
                <Box sx={{ px: 3, py: 5, textAlign: "center" }}>
                    <Typography variant="body2" color="text.secondary">Keine passenden Bestellungen gefunden.</Typography>
                </Box>
            ) : visibleOrders.map((order, index) => {
                const statusMeta = getOrderStatusMeta(order.status);
                const statusLabel = getOrderStatusLabel(order.status);
                const credits = new Intl.NumberFormat("de-DE").format(order.creditCount ?? 0);
                const isError = [UI.ORDER_STATUS.FAILED, UI.ORDER_STATUS.EXPIRED, UI.ORDER_STATUS.DISPUTED].includes(order.status);

                return (
                    <React.Fragment key={order.orderId}>
                        {index > 0 ? <Divider /> : null}
                        <Box
                            sx={{
                                ...REGISTER_STYLE.row,
                                display: "grid",
                                gridTemplateColumns: { xs: "1fr", md: "minmax(180px,1fr) 180px 160px minmax(180px,1fr) 170px" },
                                gap: 2,
                                alignItems: { xs: "start", md: "center" },
                            }}
                        >
                            <Box>
                                <Typography variant="caption" sx={REGISTER_STYLE.label}>Bestellung</Typography>
                                <Typography variant="body1">#{order.orderId}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" sx={REGISTER_STYLE.label}>Credit-Paket</Typography>
                                <Typography variant="h6">{credits}</Typography>
                                <Typography variant="caption" color="text.secondary">Credits</Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" sx={REGISTER_STYLE.label}>Erstellt</Typography>
                                <Typography variant="body2">{formatDate(order.creationTimestamp)}</Typography>
                            </Box>
                            <Tooltip title={statusMeta.text} arrow placement="top-start">
                                <Box>
                                    <Typography variant="caption" sx={REGISTER_STYLE.label}>Status</Typography>
                                    <StatusPill error={isError} muted={order.status === UI.ORDER_STATUS.OPEN}>
                                        {statusLabel}
                                    </StatusPill>
                                </Box>
                            </Tooltip>
                            <Box sx={{ display: "flex", justifyContent: { xs: "flex-start", md: "flex-end" } }}>
                                <Tooltip title={UI.TOOLTIPS.ORDER_INVOICE} arrow>
                                    <span>
                                        <Button
                                            variant="contained"
                                            startIcon={<ReceiptIcon />}
                                            disabled={!order.invoiced}
                                            onClick={() => onInvoice(order)}
                                        >
                                            Rechnung
                                        </Button>
                                    </span>
                                </Tooltip>
                            </Box>
                        </Box>
                    </React.Fragment>
                );
            })}

            <RegisterPagination
                page={page}
                pageCount={pageCount}
                total={filteredOrders.length}
                onChange={setPage}
            />
        </Paper>
    );
}

/* =========================
   LOGIN
========================= */

function Login({ setUsername, setPassword, setAdmin, showSnackbar }) {
    const [localUsername, setLocalUsername] = useState("");
    const [localPassword, setLocalPassword] = useState("");
    const [isLoggingIn, setIsLoggingIn] = useState(false);

    const handleLogin = async () => {
        if (!localUsername || !localPassword) {
            showSnackbar(UI.MESSAGES.LOGIN_MISSING_FIELDS, "warning");
            return;
        }

        setIsLoggingIn(true);
        try {
            const isAdmin = await api.login(localUsername.trim(), localPassword);
            setUsername(localUsername.trim());
            setPassword(localPassword);
            setAdmin(isAdmin);
            showSnackbar(UI.MESSAGES.LOGIN_SUCCESS, "success");
        } catch (error) {
            if (error?.status === 401 || error?.status === 403) {
                showSnackbar(UI.MESSAGES.LOGIN_INVALID, "warning");
                return;
            }
            console.error("Login error:", error);
            showSnackbar(UI.MESSAGES.LOGIN_TECHNICAL_ERROR, "error");
        } finally {
            setIsLoggingIn(false);
        }
    };

    return (
        <Paper
            component="form"
            onSubmit={(event) => {
                event.preventDefault();
                handleLogin();
            }}
            sx={{
                backgroundColor: "white",
                mt: 12.5,
                p: 4.5,
                maxWidth: 300,
                border: `15px solid ${UI.BRAND.PRIMARY_COLOR}`,
            }}
        >
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <TextField
                        value={localUsername}
                        onChange={(event) => setLocalUsername(event.target.value)}
                        label={UI.LABELS.EMAIL}
                        variant="outlined"
                        autoComplete="username"
                        disabled={isLoggingIn}
                        fullWidth
                    />
                </Grid>

                <Grid item xs={12}>
                    <TextField
                        value={localPassword}
                        onChange={(event) => setLocalPassword(event.target.value)}
                        label={UI.LABELS.PASSWORD}
                        variant="outlined"
                        type="password"
                        autoComplete="current-password"
                        disabled={isLoggingIn}
                        fullWidth
                    />
                </Grid>

                <Grid item xs={12}>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={isLoggingIn}
                        sx={{
                            ...PREMIUM_SECONDARY_ACTION_SX,
                            backgroundColor: "secondary.main",
                            "&:hover": {
                                ...PREMIUM_SECONDARY_ACTION_SX["&:hover"],
                                backgroundColor: "primary.main",
                            },
                        }}
                    >
                        {isLoggingIn ? "Anmeldung läuft …" : UI.BUTTONS.LOGIN}
                    </Button>
                </Grid>
            </Grid>
        </Paper>
    );
}

/* =========================
   APP (Tasks / Results / Orders)
========================= */

function App({ showSnackbar, username, password, isAdmin, onOpenAudienceDialog, onOpenPackagesDialog, onOpenHelp }) {
    const SHOW_OWN = 0;
    const SHOW_ALL = 1;

    const TAB_TASKS = 0;
    const TAB_RESULTS = 1;
    const TAB_ORDERS = 2;
    const TAB_USERS = 3;

    const [activeTab, setActiveTab] = useState(TAB_TASKS);
    const [viewMode, setViewMode] = useState(SHOW_OWN);
    const showAll = isAdmin && viewMode === SHOW_ALL;

    const [tasks, setTasks] = useState([]);
    const [loadingTasks, setLoadingTasks] = useState(false);

    const [results, setResults] = useState([]);
    const [loadingResults, setLoadingResults] = useState(false);

    const [orders, setOrders] = useState([]);
    const [loadingOrders, setLoadingOrders] = useState(false);

    const [focusedTaskKey, setFocusedTaskKey] = useState("");
    const [taskRotationPaused, setTaskRotationPaused] = useState(false);
    const [researchOverviewOpen, setResearchOverviewOpen] = useState(false);

    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [selectedTask, setSelectedTask] = useState(null);


    const [exportDialogOpen, setExportDialogOpen] = useState(false);
    const [exportResult, setExportResult] = useState(null);
    const [createDialogOpen, setCreateDialogOpen] = useState(false)

    const [hasLoadedTasks, setHasLoadedTasks] = useState(false);
    const [hasLoadedResults, setHasLoadedResults] = useState(false);

    const adminUsersRef = useRef(null);

    const focusedTaskIndex = useMemo(() => {
        if (!tasks.length) return -1;
        const index = tasks.findIndex((task) => taskKey(task) === focusedTaskKey);
        return index >= 0 ? index : 0;
    }, [tasks, focusedTaskKey]);

    const focusedTask = focusedTaskIndex >= 0 ? tasks[focusedTaskIndex] : null;

    const activeResearchTasks = useMemo(
        () => tasks.filter((task) => (
            task.status === UI.STATUS.TASK.RUNNING
            || task.status === UI.STATUS.TASK.SCHEDULED
        )),
        [tasks]
    );

    const activeResearchTaskIndex = useMemo(() => {
        if (!focusedTask) return -1;
        return activeResearchTasks.findIndex(
            (task) => taskKey(task) === taskKey(focusedTask)
        );
    }, [activeResearchTasks, focusedTask]);

    useEffect(() => {
        if (!tasks.length) {
            setFocusedTaskKey("");
            return;
        }
        if (!tasks.some((task) => taskKey(task) === focusedTaskKey)) {
            setFocusedTaskKey(taskKey(tasks[0]));
        }
    }, [tasks, focusedTaskKey]);

    const selectFocusedTask = useCallback((nextKey, pause = true) => {
        setFocusedTaskKey(nextKey);
        if (pause) setTaskRotationPaused(true);
    }, []);

    const moveFocusedTask = useCallback((direction, pause = true) => {
        if (!tasks.length) return;
        const currentIndex = Math.max(0, tasks.findIndex((task) => taskKey(task) === focusedTaskKey));
        const nextIndex = (currentIndex + direction + tasks.length) % tasks.length;
        setFocusedTaskKey(taskKey(tasks[nextIndex]));
        if (pause) setTaskRotationPaused(true);
    }, [tasks, focusedTaskKey]);


    const mergeResultsPreservingIdentity = useCallback((oldResults, newResults) => {
        const oldMap = new Map(oldResults.map((r) => [r.id, r]));
        return newResults.map((nr) => {
            const or = oldMap.get(nr.id);
            if (!or) return nr;

            if (or.name === nr.name && or.size === nr.size && or.paymentStatus === nr.paymentStatus && or.cleared === nr.cleared) {
                return or;
            }
            return nr;
        });
    }, []);
    const fetchTasks = useCallback(async () => {
        if (!username || !password) return;
        setLoadingTasks(true);
        try {
            const data = await api.fetchTasks(username, password, isAdmin, showAll);
            setTasks(data);
            setHasLoadedTasks(true);
        } catch {
            showSnackbar(UI.MESSAGES.LOAD_TASKS_ERROR, "error");
        } finally {
            setLoadingTasks(false);
        }
    }, [username, password, isAdmin, showAll, showSnackbar]);

    const fetchResults = useCallback(async () => {
        if (!username || !password) return;
        setLoadingResults(true);
        try {
            const data = await api.fetchResults(username, password, isAdmin, showAll);
            setResults((prev) => mergeResultsPreservingIdentity(prev, data));
            setHasLoadedResults(true)
        } catch {
            showSnackbar(UI.MESSAGES.LOAD_RESULTS_ERROR, "error");
        } finally {
            setLoadingResults(false);
        }
    }, [username, password, isAdmin, showAll, showSnackbar, mergeResultsPreservingIdentity]);

    const fetchOrders = useCallback(async () => {
        if (!username || !password) return;
        setLoadingOrders(true);
        try {
            const data = await api.fetchOrders(username, password, isAdmin, showAll);
            setOrders(data);
        } catch {
            showSnackbar(UI.MESSAGES.LOAD_ORDERS_ERROR, "error");
        } finally {
            setLoadingOrders(false);
        }
    }, [username, password, isAdmin, showAll, showSnackbar]);

    useEffect(() => {
        fetchTasks();
        if (activeTab === TAB_RESULTS) fetchResults();
        if (activeTab === TAB_ORDERS) fetchOrders();
    }, [fetchTasks, fetchResults, fetchOrders, activeTab]);

    useInterval(
        () => {
            fetchTasks();
            if (activeTab === TAB_RESULTS) fetchResults();
        },
        6000,
        Boolean(username && password)
    );

    useInterval(
        () => moveFocusedTask(1, false),
        10000,
        activeTab === TAB_TASKS && tasks.length > 1 && !taskRotationPaused && !researchOverviewOpen
    );

    const openMenu = (event, task) => {
        event.stopPropagation();
        setMenuAnchorEl(event.currentTarget);
        setSelectedTask(task);
    };

    const closeMenu = () => {
        setMenuAnchorEl(null);
        setSelectedTask(null);
    };

    const handleCancelTask = async () => {
        if (!selectedTask) return;
        try {
            await api.abortTask(username, password, selectedTask.id, selectedTask.backendId);
            showSnackbar(UI.MESSAGES.CANCEL_TASK_OK, "warning");
            closeMenu();
            fetchTasks();
        } catch {
            showSnackbar(UI.MESSAGES.CANCEL_TASK_ERROR, "error");
        }
    };

    const handlePreview = async (result) => {
        try {
            const csv = await api.previewResult(username, password, result.id);
            downloadTextAsFile(csv, `${result.name}_preview.csv`, "text/csv;charset=utf-8;");
        } catch {
            showSnackbar(UI.MESSAGES.PREVIEW_ERROR, "error");
        }
    };

    const openExportDialog = async (result) => {
        if (result.cleared) {
            try {
                const csv = await api.exportResult(username, password, result.id, result.size);
                downloadTextAsFile(csv, `${result.name}.csv`, "text/csv;charset=utf-8;");
                showSnackbar(UI.MESSAGES.EXPORT_OK, "success");
            } catch {
                showSnackbar(UI.MESSAGES.EXPORT_ERROR, "error");
            }
            return;
        }

        setExportResult(result);
        setExportDialogOpen(true);
    };

    const handleInvoice = async (order) => {
        try {
            const blob = await api.downloadInvoice(username, password, order.orderId);
            downloadBlobAsFile(blob, `invoice_${order.orderId}.pdf`);
        } catch {
            showSnackbar(UI.MESSAGES.INVOICE_ERROR, "error");
        }
    };

    const refreshCurrentView = useCallback(() => {
        if (activeTab === TAB_TASKS) fetchTasks();
        if (activeTab === TAB_RESULTS) fetchResults();
        if (activeTab === TAB_ORDERS) fetchOrders();
        if (activeTab === TAB_USERS) adminUsersRef.current?.refresh?.();
    }, [activeTab, fetchTasks, fetchResults, fetchOrders]);

    const pageHeaderProps = useMemo(() => {
        if (activeTab === TAB_TASKS) return { title: UI.PAGE_HEADERS.TASKS_TITLE, icon: <SearchIcon /> };
        if (activeTab === TAB_RESULTS) return { title: UI.PAGE_HEADERS.RESULTS_TITLE, icon: <FolderIcon /> };
        if (activeTab === TAB_ORDERS) return { title: UI.PAGE_HEADERS.ORDERS_TITLE, icon: <ReceiptIcon /> };
        if (activeTab === TAB_USERS) return { title: UI.PAGE_HEADERS.USERS_TITLE, icon: <GroupIcon /> };
        return { title: "", icon: null };
    }, [activeTab]);

    const navigationActions = [
        { key: "tasks", label: UI.TABS.TASKS, icon: <SearchIcon />, onClick: () => setActiveTab(TAB_TASKS) },
        { key: "results", label: UI.TABS.RESULTS, icon: <FolderIcon />, onClick: () => setActiveTab(TAB_RESULTS) },
        { key: "orders", label: UI.TABS.ORDERS, icon: <ReceiptIcon />, onClick: () => setActiveTab(TAB_ORDERS) },
        ...(isAdmin ? [{ key: "users", label: UI.TABS.USERS, icon: <GroupIcon />, onClick: () => setActiveTab(TAB_USERS) }] : []),
    ];

    const contextualActions = [
        { key: "refresh", label: UI.BUTTONS.REFRESH, icon: <RefreshIcon />, onClick: refreshCurrentView },
        ...(activeTab === TAB_TASKS
            ? [{ key: "new-search", label: UI.BUTTONS.NEW_SEARCH, icon: <AddIcon />, onClick: onOpenAudienceDialog }]
            : []),
        ...((activeTab === TAB_RESULTS || activeTab === TAB_ORDERS)
            ? [{ key: "buy-credits", label: UI.BUTTONS.BUY_PACKAGES, icon: <PaymentIcon />, onClick: onOpenPackagesDialog }]
            : []),
        ...(activeTab === TAB_USERS && isAdmin
            ? [{ key: "create-user", label: UI.BUTTONS.CREATE_USER, icon: <AddIcon />, onClick: () => setCreateDialogOpen(true) }]
            : []),
        ...(isAdmin
            ? [{
                key: "admin-scope",
                label: showAll ? "Admin-Modus deaktivieren" : "Admin-Modus aktivieren",
                icon: <TuneIcon />,
                onClick: () => setViewMode((current) => current === SHOW_ALL ? SHOW_OWN : SHOW_ALL),
                active: showAll,
            }]
            : []),
    ];

    const speedDialActions = [...navigationActions, ...contextualActions];

    return (
        <Box sx={{ position: "relative" }}>
            <Grid container spacing={UI.LAYOUT.PAGE_SPACING}>
                <Grid item xs={12}>
                    <Box sx={{ height: UI.LAYOUT.TOP_SPACING }} />
                </Grid>

                <Grid item xs={12}>
                    <Grid container spacing={UI.LAYOUT.CONTENT_SPACING}>
                        <Grid item xs={12}>
                            <PageHeader
                                title={pageHeaderProps.title}
                                icon={pageHeaderProps.icon}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            {activeTab === TAB_TASKS && (
                                <>
                                    {loadingTasks && tasks.length === 0 && !hasLoadedTasks ? (
                                        <CardSkeletonList />
                                    ) : tasks.length === 0 ? (
                                        <EmptyState
                                            title={UI.EMPTY.TASKS_TITLE}
                                            subtitle={UI.EMPTY.TASKS_SUBTITLE}
                                        />
                                    ) : focusedTask ? (
                                        <Box>
                                            <Fade in key={taskKey(focusedTask)} timeout={500}>
                                                <Box>
                                                    <TaskCard
                                                        task={focusedTask}
                                                        onOpenMenu={openMenu}
                                                        position={focusedTaskIndex + 1}
                                                        total={tasks.length}
                                                    />
                                                </Box>
                                            </Fade>

                                            {activeResearchTasks.length > 1 && activeResearchTaskIndex >= 0 && (
                                                <ResearchCompanionBar
                                                    paused={taskRotationPaused}
                                                    taskCount={activeResearchTasks.length}
                                                    activeIndex={activeResearchTaskIndex}
                                                    onTogglePause={() => setTaskRotationPaused((current) => !current)}
                                                    onOpenOverview={() => setResearchOverviewOpen(true)}
                                                />
                                            )}
                                        </Box>
                                    ) : null}
                                </>
                            )}

                            {activeTab === TAB_RESULTS && (
                                <>
                                    {loadingResults && results.length === 0 && !hasLoadedResults ? (
                                        <CardSkeletonList />
                                    ) : results.length === 0 ? (
                                        <EmptyState
                                            title={UI.EMPTY.RESULTS_TITLE}
                                            subtitle={UI.EMPTY.RESULTS_SUBTITLE}
                                        />
                                    ) : (
                                        <ResultList
                                            results={results}
                                            onPreview={handlePreview}
                                            onExport={openExportDialog}
                                        />
                                    )}
                                </>
                            )}

                            {activeTab === TAB_ORDERS && (
                                <>
                                    {loadingOrders && orders.length === 0 ? (
                                        <CardSkeletonList />
                                    ) : orders.length === 0 ? (
                                        <EmptyState
                                            title={UI.EMPTY.ORDERS_TITLE}
                                            subtitle={UI.EMPTY.ORDERS_SUBTITLE}
                                        />
                                    ) : (
                                        <OrderList orders={orders} onInvoice={handleInvoice} />
                                    )}
                                </>
                            )}

                            {activeTab === TAB_USERS && isAdmin && (
                                <AdminUserList
                                    ref={adminUsersRef}
                                    username={username}
                                    password={password}
                                    showSnackbar={showSnackbar}
                                    createDialogOpen={createDialogOpen}
                                    setCreateDialogOpen={setCreateDialogOpen}
                                />
                            )}
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>

            <Box
                sx={{
                    position: "fixed",
                    bottom: `calc(${UI.LAYOUT.SPEED_DIAL_BOTTOM}px + env(safe-area-inset-bottom))`,
                    right: { xs: 16, sm: UI.LAYOUT.SPEED_DIAL_RIGHT },
                    zIndex: 1050,
                }}
            >
                <SpeedDial
                    ariaLabel={showAll ? "Navigation – Admin-Modus aktiv" : "Navigation"}
                    icon={<MenuIcon />}
                    FabProps={{
                        sx: {
                            backgroundColor: showAll ? "success.main" : UI.BRAND.PRIMARY_COLOR,
                            "&:hover": {
                                backgroundColor: showAll ? "success.dark" : "secondary.main",
                            },
                            transition: "background-color .2s ease, box-shadow .2s ease",
                            ...(showAll ? {
                                boxShadow: "0 0 0 4px rgba(46, 125, 50, 0.16), 0 8px 18px rgba(46, 125, 50, 0.28)",
                            } : {}),
                        },
                    }}
                >
                    {speedDialActions.map((item) => (
                        <SpeedDialAction
                            key={item.key}
                            icon={item.icon}
                            tooltipTitle={item.label}
                            onClick={item.onClick}
                            FabProps={item.active ? {
                                sx: {
                                    bgcolor: "success.main",
                                    color: "success.contrastText",
                                    "&:hover": { bgcolor: "success.dark" },
                                },
                            } : undefined}
                        />
                    ))}
                </SpeedDial>
            </Box>

            <ResearchOverviewDialog
                open={researchOverviewOpen}
                onClose={() => setResearchOverviewOpen(false)}
                tasks={tasks}
                onOpenTask={(task) => {
                    selectFocusedTask(taskKey(task), true);
                    setResearchOverviewOpen(false);
                    setActiveTab(TAB_TASKS);
                }}
            />

            <Menu disableScrollLock anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={closeMenu}>
                {selectedTask && (selectedTask.status === UI.STATUS.TASK.RUNNING || selectedTask.status === UI.STATUS.TASK.SCHEDULED) && (
                    <MenuItem onClick={handleCancelTask}>{UI.MENUS.CANCEL_SEARCH}</MenuItem>
                )}
            </Menu>

            <ExportDialog
                open={exportDialogOpen}
                onClose={() => setExportDialogOpen(false)}
                result={exportResult}
                username={username}
                password={password}
                showSnackbar={showSnackbar}
                onBuyCredits={() => {
                    setExportDialogOpen(false);
                    onOpenPackagesDialog();
                }}
            />
        </Box>
    );
}




const SectionCard = ({ icon, title, children }) => (
    <Paper
        variant="outlined"
        sx={{
            borderRadius: 2,
            p: 2.5,
            mb: 3,
            bgcolor: "rgba(0,0,0,0.015)",
        }}
    >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2 }}>
            {icon}
            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                {title}
            </Typography>
        </Box>
        {children}
    </Paper>
);



const AdminUserEditDialog = ({
    open,
    onClose,
    user,
    username,
    password,
    showSnackbar,
    onSaved,
}) => {
    const [saving, setSaving] = useState(false);
    const [newPassword, setNewPassword] = useState("");
    const [errors, setErrors] = useState({});

    const [draft, setDraft] = useState(() => ({
        businessName: user.businessName ?? "",
        street: user.street ?? "",
        houseNo: user.houseNo ?? "",
        zipCode: user.zipCode ?? "",
        town: user.town ?? "",
        country: user.country ?? "",

        bank: user.bank ?? "",
        iban: user.iban ?? "",
        bic: user.bic ?? "",

        vatId: user.vatId ?? "",
        taxId: user.taxId ?? "",

        creditsBalance: user.creditsBalance ?? 0,
        noPayment: Boolean(user.noPayment),
        smallBusiness: Boolean(user.smallBusiness),
        disabled: Boolean(user.disabled),
    }));

    const validateField = (name, value, regex) => {
        setErrors((e) => ({
            ...e,
            [name]: !regex.test(value),
        }));
    };

    useEffect(() => {
        if (!open) return;

        const nextDraft = {
            businessName: user.businessName ?? "",

            street: user.street ?? "",
            houseNo: user.houseNo ?? "",
            zipCode: user.zipCode ?? "",
            town: user.town ?? "",
            country: user.country ?? "",

            bank: user.bank ?? "",
            iban: user.iban ?? "",
            bic: user.bic ?? "",

            vatId: user.vatId ?? "",
            taxId: user.taxId ?? "",

            creditsBalance: user.creditsBalance ?? 0,
            noPayment: Boolean(user.noPayment),
            smallBusiness: Boolean(user.smallBusiness),
            disabled: Boolean(user.disabled),
        };

        setDraft(nextDraft);
        setNewPassword("");
        setErrors(getAdminUserValidationErrors(nextDraft));
    }, [open, user]);

    const save = async () => {
        setSaving(true);
        try {

            const addressDTO = {
                street: draft.street,
                houseNo: draft.houseNo,
                zipCode: draft.zipCode,
                town: draft.town,
                country: draft.country,
            };

            const bankingDTO = {
                bank: draft.bank,
                iban: draft.iban,
                bic: draft.bic,
            };

            const taxDTO = {
                vatId: draft.vatId,
                taxId: draft.taxId,
            };

            let updated = await api.setBusinessName(username, password, user.id, draft.businessName);
            updated = await api.setAddress(username, password, user.id, addressDTO);
            updated = await api.setBanking(username, password, user.id, bankingDTO);
            updated = await api.setTax(username, password, user.id, taxDTO);
            updated = await api.setCreditsBalance(username, password, user.id, draft.creditsBalance);
            updated = await api.setNoPayment(username, password, user.id, draft.noPayment);
            updated = await api.setSmallBusiness(username, password, user.id, draft.smallBusiness);
            updated = await api.setDisabled(username, password, user.id, draft.disabled);
            showSnackbar(UI.MESSAGES.ADMIN_UPDATE_OK, "success");
            onSaved(updated);
            onClose();
        } catch (e) {
            console.error(e);
            showSnackbar(UI.MESSAGES.ADMIN_UPDATE_FAILED, "error");
        } finally {
            setSaving(false);
        }
    };

    return (
        <ResponsiveDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>{UI.DIALOGS.USER_EDIT_TITLE}</DialogTitle>

            <DialogContent dividers sx={{ pt: 3 }}>
                {/* =====================
                    ADDRESS
                ===================== */}
                <SectionCard
                    icon={<AccountCircleIcon color="primary" />}
                    title="Firma"
                >
                    <TextField
                        label="Firmenname"
                        fullWidth
                        variant="outlined"
                        value={draft.businessName}
                        error={!!errors.businessName}

                        onChange={
                            (e) => {
                                setDraft((d) => ({ ...d, businessName: e.target.value }))
                                validateField("businessName", e.target.value, REGEX.BUSINESS_NAME);
                            }
                        }
                    />
                </SectionCard>
                <SectionCard
                    icon={<LocationOnIcon color="primary" />}
                    title="Adresse"
                >
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={8}>
                            <TextField
                                label="Straße"
                                fullWidth
                                variant="outlined"
                                value={draft.street}
                                error={!!errors.street}
                                onChange={
                                    (e) => {
                                        setDraft((d) => ({ ...d, street: e.target.value }))
                                        validateField("street", e.target.value, REGEX.STREET);
                                    }
                                }
                            />
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <TextField
                                label="Hausnummer"
                                fullWidth
                                variant="outlined"
                                value={draft.houseNo}
                                error={!!errors.houseNo}
                                onChange={
                                    (e) => {
                                        setDraft((d) => ({ ...d, houseNo: e.target.value }))
                                        validateField("houseNo", e.target.value, REGEX.HOUSE_NO);
                                    }
                                }
                            />
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <TextField
                                label="PLZ"
                                fullWidth
                                variant="outlined"
                                value={draft.zipCode}
                                error={!!errors.zipCode}
                                onChange={
                                    (e) => {
                                        setDraft((d) => ({ ...d, zipCode: e.target.value }))
                                        validateField("zipCode", e.target.value, REGEX.ZIP);
                                    }
                                }
                            />
                        </Grid>

                        <Grid item xs={12} sm={8}>
                            <TextField
                                label="Ort"
                                fullWidth
                                variant="outlined"
                                value={draft.town}
                                error={!!errors.town}
                                onChange={
                                    (e) => {
                                        setDraft((d) => ({ ...d, town: e.target.value }))
                                        validateField("town", e.target.value, REGEX.TOWN);

                                    }
                                }
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                label="Land"
                                disabled={true}
                                fullWidth
                                variant="outlined"
                                value={UI.COUNTRY[draft.country]}
                                onChange={(e) =>
                                    setDraft((d) => ({ ...d, country: e.target.value }))
                                }
                            />
                        </Grid>
                    </Grid>
                </SectionCard>

                {/* =====================
                    BANKING
                ===================== */}
                <SectionCard
                    icon={<AccountBalanceIcon color="primary" />}
                    title="Bankverbindung"
                >
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                label="Bank"
                                fullWidth
                                variant="outlined"
                                value={draft.bank}
                                error={!!errors.bank}
                                onChange={
                                    (e) => {
                                        setDraft((d) => ({ ...d, bank: e.target.value }))
                                        validateField("bank", e.target.value, REGEX.BANK);
                                    }
                                }
                            />
                        </Grid>

                        <Grid item xs={12} sm={8}>
                            <TextField
                                label="IBAN"
                                fullWidth
                                variant="outlined"
                                value={draft.iban}
                                error={!!errors.iban}
                                onChange={(e) => {
                                    const val = e.target.value.toUpperCase();
                                    setDraft((d) => ({ ...d, iban: e.target.value }))
                                    validateField("iban", val, REGEX.IBAN);
                                }
                                }
                            />
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <TextField
                                label="BIC"
                                fullWidth
                                variant="outlined"
                                value={draft.bic}
                                error={!!errors.bic}
                                onChange={
                                    (e) => {
                                        setDraft((d) => ({ ...d, bic: e.target.value }))
                                        validateField("bic", e.target.value, REGEX.BIC);
                                    }
                                }
                            />
                        </Grid>
                    </Grid>
                </SectionCard>

                {/* =====================
                    TAX
                ===================== */}
                <SectionCard
                    icon={<ReceiptLongIcon color="primary" />}
                    title="Steuerinformationen"
                >
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="USt-IdNr."
                                fullWidth
                                variant="outlined"
                                value={draft.vatId}
                                error={!!errors.vatId}
                                onChange={
                                    (e) => {
                                        setDraft((d) => ({ ...d, vatId: e.target.value }))
                                        validateField("vatId", e.target.value, REGEX.VAT_ID);
                                    }
                                }
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Steuernummer"
                                fullWidth
                                variant="outlined"
                                value={draft.taxId}
                                error={!!errors.taxId}
                                onChange={
                                    (e) => {
                                        setDraft((d) => ({ ...d, taxId: e.target.value }))
                                        validateField("taxId", e.target.value, REGEX.TAX_ID);
                                    }
                                }
                            />
                        </Grid>
                    </Grid>
                </SectionCard>
                <SectionCard
                    icon={<TuneIcon color="primary" />}
                    title="Kontostatus"
                >
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <FormControlLabel
                                label={UI.LABELS.NO_PAYMENT}
                                control={
                                    <Switch
                                        checked={Boolean(draft.noPayment)}
                                        onChange={(event) => setDraft((current) => ({ ...current, noPayment: event.target.checked }))}
                                    />
                                }
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                label={UI.LABELS.SMALL_BUSINESS}
                                control={
                                    <Switch
                                        checked={Boolean(draft.smallBusiness)}
                                        onChange={(event) => setDraft((current) => ({ ...current, smallBusiness: event.target.checked }))}
                                    />
                                }
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                label={UI.LABELS.USER_DISABLED}
                                control={
                                    <Switch
                                        color="error"
                                        checked={Boolean(draft.disabled)}
                                        onChange={(event) => setDraft((current) => ({ ...current, disabled: event.target.checked }))}
                                    />
                                }
                            />
                        </Grid>
                    </Grid>
                </SectionCard>
                <SectionCard
                    icon={<LockIcon color="primary" />}
                    title={UI.SECTIONS.SECURITY}
                >
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={8}>
                            <TextField
                                label={UI.LABELS.NEW_PASSWORD}
                                type="password"
                                fullWidth
                                variant="outlined"
                                value={newPassword}
                                error={!!errors.password}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    setNewPassword(val);
                                    validateField("password", val, REGEX.PASSWORD);
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4} sx={{ display: "flex", alignItems: "center" }}>
                            <Button
                                variant="contained"
                                disabled={!newPassword || errors.password || saving}
                                onClick={async () => {
                                    try {
                                        setSaving(true);
                                        await api.setUserPassword(
                                            username,
                                            password,
                                            user.id,
                                            newPassword
                                        );
                                        showSnackbar(UI.MESSAGES.PASSWORD_SET_OK, "success");
                                        setNewPassword("");
                                    } catch {
                                        showSnackbar(UI.MESSAGES.PASSWORD_SET_ERROR, "error");
                                    } finally {
                                        setSaving(false);
                                    }
                                }}
                            >
                                {UI.BUTTONS.SAVE}
                            </Button>
                        </Grid>
                    </Grid>
                </SectionCard>
                <SectionCard
                    icon={<PaymentIcon color="primary" />}
                    title="Credits"
                >
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12}>
                            <TextField
                                label={UI.LABELS.CREDITS}
                                type="number"
                                fullWidth
                                variant="outlined"
                                value={draft.creditsBalance}
                                inputProps={{ min: 0 }}
                                onChange={(e) => {
                                    const val = Number(e.target.value);
                                    setDraft((d) => ({
                                        ...d,
                                        creditsBalance: Number.isNaN(val) ? 0 : val,
                                    }));
                                }}
                            />
                        </Grid>
                    </Grid>
                </SectionCard>

            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>{UI.BUTTONS.CANCEL}</Button>
                <Button
                    variant="contained"
                    sx={PREMIUM_SECONDARY_ACTION_SX}
                    onClick={save}
                    disabled={saving || Object.values(errors).some(Boolean)}
                >
                    {UI.BUTTONS.SAVE}
                </Button>
            </DialogActions>
        </ResponsiveDialog>
    );
};

function CreateUserDialog({
    open,
    onClose,
    username,
    password,
    onCreated,
    showSnackbar,
}) {
    const [userEmail, setUserEmail] = useState("");
    const [businessName, setBusinessName] = useState("");
    const [loading, setLoading] = useState(false);

    const [isEmailValid, setIsEmailValid] = useState(true);
    const [isBusinessNameValid, setIsBusinessNameValid] = useState(true);

    useEffect(() => {
        if (!open) return;
        setUserEmail("");
        setBusinessName("");
        setIsEmailValid(true);
        setIsBusinessNameValid(true);
    }, [open]);

    const handleCreate = async () => {
        if (
            !userEmail ||
            !businessName ||
            !isEmailValid ||
            !isBusinessNameValid
        ) {
            showSnackbar(UI.MESSAGES.USER_CREATE_MISSING_FIELDS, "warning");
            return;
        }

        setLoading(true);
        try {
            const newUser = await api.createUser(
                username,
                password,
                userEmail.trim(),
                businessName.trim()
            );

            showSnackbar(UI.MESSAGES.USER_CREATE_OK, "success");
            onCreated?.(newUser);
            onClose();
            setUserEmail("");
            setBusinessName("");
            setIsEmailValid(true);
            setIsBusinessNameValid(true);
        } catch {
            showSnackbar(UI.MESSAGES.USER_CREATE_ERROR, "error");
        } finally {
            setLoading(false);
        }
    };

    return (
        <ResponsiveDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{UI.DIALOGS.CREATE_USER_TITLE}</DialogTitle>

            <DialogContent dividers>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <TextField
                        label={UI.LABELS.USER_EMAIL}
                        value={userEmail}
                        error={!isEmailValid}
                        onChange={(e) => {
                            const val = e.target.value;
                            setUserEmail(val);
                            setIsEmailValid(REGEX.EMAIL.test(val));
                        }}
                        fullWidth
                    />

                    <TextField
                        label={UI.LABELS.BUSINESS_NAME}
                        value={businessName}
                        error={!isBusinessNameValid}

                        onChange={(e) => {
                            const val = e.target.value;
                            setBusinessName(val);
                            setIsBusinessNameValid(REGEX.BUSINESS_NAME.test(val));
                        }}
                        fullWidth
                    />
                </Box>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>
                    {UI.BUTTONS.CANCEL}
                </Button>

                <Button
                    variant="contained"
                    sx={PREMIUM_SECONDARY_ACTION_SX}
                    onClick={handleCreate}
                    disabled={
                        loading ||
                        !isEmailValid ||
                        !isBusinessNameValid ||
                        !userEmail ||
                        !businessName
                    }
                >
                    {UI.BUTTONS.CREATE_USER}
                </Button>
            </DialogActions>
        </ResponsiveDialog>
    );
}


const AdminUserList = React.forwardRef(function AdminUserList({ username, password, showSnackbar, createDialogOpen, setCreateDialogOpen }, ref) {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [page, setPage] = useState(1);
    const [searchQuery, setSearchQuery] = useState("");

    const filteredUsers = useMemo(() => {
        const query = searchQuery.trim().toLocaleLowerCase("de-DE");
        if (!query) return users;
        return users.filter((user) => {
            const values = [
                user?.businessName,
                user?.emailAddress,
                user?.creditsBalance,
                user?.admin ? "administrator admin" : "benutzer",
                user?.disabled ? "deaktiviert" : "aktiv",
                user?.noPayment ? UI.LABELS.NO_PAYMENT : "",
                user?.smallBusiness ? UI.LABELS.SMALL_BUSINESS : "",
            ];
            return values.some((value) => String(value ?? "").toLocaleLowerCase("de-DE").includes(query));
        });
    }, [users, searchQuery]);

    const pageCount = Math.max(1, Math.ceil(filteredUsers.length / REGISTER_PAGE_SIZE));
    const visibleUsers = filteredUsers.slice((page - 1) * REGISTER_PAGE_SIZE, page * REGISTER_PAGE_SIZE);

    useEffect(() => {
        if (page > pageCount) setPage(pageCount);
    }, [page, pageCount]);

    useEffect(() => {
        setPage(1);
    }, [searchQuery, users.length]);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const data = await api.fetchUsers(username, password);
            setUsers(data);
        } catch {
            showSnackbar(UI.MESSAGES.ADMIN_USERS_LOAD_ERROR, "error");
            setUsers([]);
        } finally {
            setLoading(false);
        }
    }, [username, password, showSnackbar]);

    useEffect(() => {
        if (!ref) return;
        ref.current = { refresh };
    }, [ref, refresh]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const updateUser = (updated) => {
        setUsers((prev) => prev.map((u) => (u.id === updated.id ? updated : u)));
    };

    const openEdit = (user) => {
        setSelectedUser(user);
        setEditOpen(true);
    };

    if (loading && users.length === 0) return <CardSkeletonList />;

    return (
        <>
            <Paper variant="outlined" sx={REGISTER_STYLE.surface}>
                <RegisterHeaderSearch
                    value={searchQuery}
                    onChange={setSearchQuery}
                    placeholder="Benutzer durchsuchen"
                />
                <Box
                    sx={{
                        ...REGISTER_STYLE.columnHeader,
                        gridTemplateColumns: "minmax(260px,1.55fr) 150px 190px minmax(180px,1fr) 145px",
                        pt: 1.1,
                    }}
                >
                    <Typography variant="caption" color="text.secondary">Unternehmen / Login</Typography>
                    <Typography variant="caption" color="text.secondary">Credits</Typography>
                    <Typography variant="caption" color="text.secondary">Zugriff</Typography>
                    <Typography variant="caption" color="text.secondary">Abrechnung</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ textAlign: "right" }}>Aktion</Typography>
                </Box>

                {visibleUsers.length === 0 ? (
                    <Box sx={{ px: 3, py: 5, textAlign: "center" }}>
                        <Typography variant="body2" color="text.secondary">Keine passenden Benutzer gefunden.</Typography>
                    </Box>
                ) : visibleUsers.map((user, index) => {
                    const creditLabel = new Intl.NumberFormat("de-DE").format(user.creditsBalance ?? 0);
                    const billingNotes = [
                        user.noPayment ? UI.LABELS.NO_PAYMENT : null,
                        user.smallBusiness ? UI.LABELS.SMALL_BUSINESS : null,
                    ].filter(Boolean);

                    return (
                        <React.Fragment key={user.id}>
                            {index > 0 ? <Divider /> : null}
                            <Box
                                sx={{
                                    ...REGISTER_STYLE.row,
                                    display: "grid",
                                    gridTemplateColumns: { xs: "1fr", md: "minmax(260px,1.55fr) 150px 190px minmax(180px,1fr) 145px" },
                                    gap: { xs: 2, md: 2 },
                                    alignItems: { xs: "start", md: "center" },
                                }}
                            >
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography variant="caption" sx={REGISTER_STYLE.label}>Unternehmen / Login</Typography>
                                    <Typography variant="body1" sx={{ overflowWrap: "anywhere" }}>
                                        {user.businessName || "–"}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35, overflowWrap: "anywhere" }}>
                                        {user.emailAddress}
                                    </Typography>
                                </Box>

                                <Box>
                                    <Typography variant="caption" sx={REGISTER_STYLE.label}>Credits</Typography>
                                    <Typography variant="h6">{creditLabel}</Typography>
                                    {user.creditsUpdatedAt ? (
                                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                                            {formatDate(user.creditsUpdatedAt)}
                                        </Typography>
                                    ) : null}
                                </Box>

                                <Box>
                                    <Typography variant="caption" sx={REGISTER_STYLE.label}>Zugriff</Typography>
                                    <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                                        <StatusPill>{user.admin ? "Administrator" : "Benutzer"}</StatusPill>
                                        <StatusPill muted={!user.disabled} error={user.disabled}>
                                            {user.disabled ? "Deaktiviert" : "Aktiv"}
                                        </StatusPill>
                                    </Box>
                                </Box>

                                <Box>
                                    <Typography variant="caption" sx={REGISTER_STYLE.label}>Abrechnung</Typography>
                                    {billingNotes.length ? (
                                        <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                                            {billingNotes.map((note) => <StatusPill key={note} muted>{note}</StatusPill>)}
                                        </Box>
                                    ) : (
                                        <Typography variant="body2" color="text.secondary">Standard</Typography>
                                    )}
                                </Box>

                                <Box sx={{ display: "flex", justifyContent: { xs: "flex-start", md: "flex-end" } }}>
                                    <Button
                                        variant="contained"
                                        startIcon={<AccountCircleIcon />}
                                        onClick={() => openEdit(user)}
                                    >
                                        {UI.BUTTONS.EDIT}
                                    </Button>
                                </Box>
                            </Box>
                        </React.Fragment>
                    );
                })}

                <RegisterPagination
                    page={page}
                    pageCount={pageCount}
                    total={filteredUsers.length}
                    onChange={setPage}
                />
            </Paper>

            {selectedUser && (
                <AdminUserEditDialog
                    open={editOpen}
                    onClose={() => setEditOpen(false)}
                    user={selectedUser}
                    username={username}
                    password={password}
                    showSnackbar={showSnackbar}
                    onSaved={(updated) => {
                        updateUser(updated);
                        setSelectedUser(updated);
                    }}
                />
            )}
            <CreateUserDialog
                open={createDialogOpen}
                onClose={() => setCreateDialogOpen(false)}
                username={username}
                password={password}
                showSnackbar={showSnackbar}
                onCreated={() => ref.current?.refresh()}
            />
        </>
    );
});


function UsageDialog({ email, password, open, onClose, showSnackbar }) {
    const [data, setData] = useState([]);
    const [months, setMonths] = useState(12);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const loadUsage = useCallback(async () => {
        if (!open) return;
        setLoading(true);
        setError(null);
        try {
            const result = await api.fetchUsage(email, password, months);
            setData(mapUsageToChartData(result));
        } catch (err) {
            showSnackbar(UI.MESSAGES.USAGE_DATA_LOADING_FAILED, "error");
            setData([]);
        } finally {
            setLoading(false);
        }
    }, [email, password, months, open, showSnackbar]);

    useEffect(() => {
        loadUsage();
    }, [loadUsage]);

    return (
        <ResponsiveDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                {UI.DIALOGS.DOWNLOAD_USAGE_TITLE}
            </DialogTitle>

            <DialogContent dividers>
                {/* Zeitraum-Auswahl */}
                <Box sx={{ mb: 2, maxWidth: 240 }}>
                    <FormControl fullWidth size="small">
                        <InputLabel>{UI.LABELS.MONTHS}</InputLabel>
                        <Select
                            label={UI.LABELS.MONTHS}
                            value={months}
                            onChange={(e) => setMonths(e.target.value)}
                        >
                            {UI.MONTH_OPTIONS.map((m) => (
                                <MenuItem key={m} value={m}>
                                    {m} {UI.LABELS.MONTH}{m !== 1 ? "e" : ""}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                <Divider sx={{ mb: 2 }} />

                {/* Content */}
                {loading && (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                        <CircularProgress />
                    </Box>
                )}

                {error && (
                    <Alert severity="error">
                        {error}
                    </Alert>
                )}

                {!loading && !error && data.length > 0 && (
                    <Box sx={{ width: "100%", height: 360, marginTop: "50px" }}>
                        <ResponsiveContainer>
                            <LineChart data={data}>
                                <XAxis dataKey="label" />
                                <YAxis />
                                <Tooltip />
                                <Legend />

                                <Line
                                    type="monotone"
                                    dataKey="credits"
                                    name={UI.LABELS.CREDITS}
                                    stroke={UI.BRAND.PRIMARY_COLOR}
                                    strokeWidth={2}
                                />

                                <Line
                                    type="monotone"
                                    dataKey="downloads"
                                    name={UI.LABELS.DOWNLOADS}
                                    stroke="#2e7d32"
                                    strokeWidth={2}
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    </Box>
                )}

                {!loading && !error && data.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                        {UI.MESSAGES.NO_DATA_AVAILABLE}
                    </Typography>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>
                    {UI.BUTTONS.CLOSE}
                </Button>
            </DialogActions>
        </ResponsiveDialog>
    );
}


/* =========================
   MAIN (Shell)
========================= */

function Main() {
    const { snackbarState, showSnackbar, closeSnackbar } = useSnackbar();

    const [username, setUsername] = useState(null);
    const [password, setPassword] = useState(null);
    const [isAdmin, setAdmin] = useState(null);

    const [isUsageDialogOpen, setIsUsageDialogOpen] = useState(false);
    const [isImprintDialogOpen, setIsImprintDialogOpen] = useState(false);
    const [isSupportDialogOpen, setIsSupportDialogOpen] = useState(false);
    const [isAudienceDialogOpen, setIsAudienceDialogOpen] = useState(false);
    const [isPackageDialogOpen, setIsPackageDialogOpen] = useState(false);
    const [isProfileDialogOpen, setIsProfileDialogOpen] = useState(false);
    const [isHelpOpen, setIsHelpOpen] = useState(false);

    const [userMenuAnchor, setUserMenuAnchor] = useState(null);
    const userMenuOpen = Boolean(userMenuAnchor);

    const openUserMenu = (event) => setUserMenuAnchor(event.currentTarget);
    const closeUserMenu = () => setUserMenuAnchor(null);

    const doLogout = () => {
        closeUserMenu();
        setUsername(null);
        setPassword(null);
        setAdmin(null);
    };

    const isLoggedIn = username !== null;

    return (
        <ThemeProvider theme={premiumTheme}>
            <CssBaseline />
            <Box
                sx={{
                    minHeight: "100vh",
                    display: "flex",
                    flexDirection: "column",
                    ...(isLoggedIn
                        ? {}
                        : {
                            backgroundImage: `url("${UI.BRAND.LOGIN_SCREEN_BACKGROUND_IMAGE}")`,
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                            backgroundRepeat: "no-repeat",
                            backgroundAttachment: { xs: "scroll", md: "fixed" },
                        }),
                }}
            >
                {/* Nexus-equivalent header spacer */}
                <Box sx={{ minHeight: `calc(${UI.LAYOUT.HEADER_HEIGHT}px + env(safe-area-inset-top)) !important`, flexShrink: 0 }} />

                {/* Nexus-equivalent fixed header; only the product logo differs. */}
                <Box
                    component="header"
                    sx={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100%",
                        minHeight: `calc(${UI.LAYOUT.HEADER_HEIGHT}px + env(safe-area-inset-top)) !important`,
                        pt: "env(safe-area-inset-top)",
                        backgroundColor: UI.BRAND.PRIMARY_COLOR,
                        borderBottom: "2px solid white",
                        zIndex: 1100,
                        display: "grid",
                        gridTemplateColumns: "48px minmax(0, 1fr) 48px",
                        alignItems: "center",
                        px: { xs: 1.5, sm: 3 },
                    }}
                >
                    <Box sx={{ width: 48, height: 48 }} aria-hidden="true" />

                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", justifySelf: "center" }}>
                        <Box
                            component="img"
                            src={UI.BRAND.PRODUCT_LOGO_URL}
                            alt={UI.BRAND.ALT_LOGO}
                            sx={{ display: "block", width: 120, maxWidth: "100%" }}
                        />
                    </Box>

                    <IconButton
                        id="header-context-menu-button"
                        aria-label="Kontextmenü öffnen"
                        aria-controls={userMenuOpen ? "header-context-menu" : undefined}
                        aria-haspopup="true"
                        aria-expanded={userMenuOpen ? "true" : undefined}
                        onClick={openUserMenu}
                        sx={{
                            justifySelf: "end",
                            color: "white",
                            "&:hover": { backgroundColor: "rgba(255,255,255,.12)" },
                        }}
                    >
                        <MoreVertIcon />
                    </IconButton>
                </Box>

                <Menu
                    disableScrollLock
                    id="header-context-menu"
                    anchorEl={userMenuAnchor}
                    open={userMenuOpen}
                    onClose={closeUserMenu}
                    MenuListProps={{ "aria-labelledby": "header-context-menu-button" }}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                    PaperProps={{
                        sx: {
                            minWidth: 240,
                            mt: 1,
                            borderRadius: 2,
                            border: 1,
                            borderColor: "divider",
                            boxShadow: "0 8px 30px rgba(15, 23, 42, 0.14)",
                        },
                    }}
                    sx={{ zIndex: 1301 }}
                >
                    <MenuItem
                        onClick={() => {
                            closeUserMenu();
                            setIsImprintDialogOpen(true);
                        }}
                        sx={{ gap: 1.5, py: 1.25 }}
                    >
                        <DescriptionOutlinedIcon fontSize="small" sx={{ color: UI.BRAND.PRIMARY_COLOR }} />
                        <Typography component="span" variant="body1">{UI.MENUS.IMPRINT}</Typography>
                    </MenuItem>

                    <MenuItem
                        onClick={() => {
                            closeUserMenu();
                            setIsSupportDialogOpen(true);
                        }}
                        sx={{ gap: 1.5, py: 1.25 }}
                    >
                        <HelpOutlineIcon fontSize="small" sx={{ color: UI.BRAND.PRIMARY_COLOR }} />
                        <Typography component="span" variant="body1">{UI.MENUS.SUPPORT}</Typography>
                    </MenuItem>

                    {isLoggedIn ? (
                        <>
                            <Divider sx={{ my: 0.5 }} />

                            <MenuItem
                                onClick={() => {
                                    closeUserMenu();
                                    setIsProfileDialogOpen(true);
                                }}
                                sx={{ gap: 1.5, py: 1.25 }}
                            >
                                <AccountCircleIcon fontSize="small" sx={{ color: UI.BRAND.PRIMARY_COLOR }} />
                                <Typography component="span" variant="body1">{UI.MENUS.PROFILE}</Typography>
                            </MenuItem>

                            <MenuItem
                                onClick={() => {
                                    closeUserMenu();
                                    setIsUsageDialogOpen(true);
                                }}
                                sx={{ gap: 1.5, py: 1.25 }}
                            >
                                <DownloadIcon fontSize="small" sx={{ color: UI.BRAND.PRIMARY_COLOR }} />
                                <Typography component="span" variant="body1">{UI.BUTTONS.DOWNLOAD_USAGE}</Typography>
                            </MenuItem>

                            <MenuItem
                                onClick={() => {
                                    closeUserMenu();
                                    setIsHelpOpen(true);
                                }}
                                sx={{ gap: 1.5, py: 1.25 }}
                            >
                                <ArticleIcon fontSize="small" sx={{ color: UI.BRAND.PRIMARY_COLOR }} />
                                <Typography component="span" variant="body1">{UI.MENUS.HELP}</Typography>
                            </MenuItem>

                            <Divider sx={{ my: 0.5 }} />

                            <MenuItem onClick={doLogout} sx={{ gap: 1.5, py: 1.25, color: "error.main" }}>
                                <LogoutIcon fontSize="small" sx={{ color: "error.main" }} />
                                <Typography component="span" variant="body1">{UI.MENUS.LOGOUT}</Typography>
                            </MenuItem>
                        </>
                    ) : null}
                </Menu>

                <Box sx={{ width: "100%", flexGrow: 1 }}>
                    <Box
                        component="main"
                        sx={{
                            display: "block",
                            width: { xs: "calc(100% - 24px)", sm: UI.LAYOUT.CONTENT_WIDTH },
                            maxWidth: "100%",
                            mx: "auto",
                            pt: UI.LAYOUT.CONTENT_PADDING_TOP,
                            pb: UI.LAYOUT.CONTENT_PADDING_BOTTOM,
                            position: "relative",
                            zIndex: 0,
                            fontFamily: "Arial, Helvetica, sans-serif",
                        }}
                    >
                        {!isLoggedIn ? (
                            <Login
                                setUsername={setUsername}
                                setPassword={setPassword}
                                setAdmin={setAdmin}
                                showSnackbar={showSnackbar}
                            />
                        ) : (
                            <App
                                showSnackbar={showSnackbar}
                                username={username}
                                password={password}
                                isAdmin={isAdmin}
                                onOpenAudienceDialog={() => setIsAudienceDialogOpen(true)}
                                onOpenPackagesDialog={() => setIsPackageDialogOpen(true)}
                                onOpenHelp={() => setIsHelpOpen(true)}
                            />
                        )}
                    </Box>
                </Box>

                {/* Nexus-equivalent footer spacer + fixed footer */}
                <Box sx={{ height: `calc(${UI.LAYOUT.FOOTER_HEIGHT}px + env(safe-area-inset-bottom))`, flexShrink: 0 }} />
                <Box
                    component="footer"
                    sx={{
                        position: "fixed",
                        left: 0,
                        bottom: 0,
                        width: "100%",
                        borderTop: "2px solid white",
                        zIndex: 1090,
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            px: { xs: 2, sm: 3 },
                            pt: 4,
                            pb: "calc(32px + env(safe-area-inset-bottom))",
                            backgroundColor: UI.BRAND.PRIMARY_COLOR,
                        }}
                    >
                        <Box
                            component="img"
                            src={UI.BRAND.LOGO_URL}
                            alt={UI.FOOTER.TEXT}
                            sx={{ display: "block", width: "100%", maxWidth: 220, maxHeight: 30, objectFit: "contain" }}
                        />
                    </Box>
                </Box>

                <HelpCenterDrawer
                    open={isHelpOpen}
                    onClose={() => setIsHelpOpen(false)}
                    showSnackbar={showSnackbar}
                    onOpenSupport={() => {
                        setIsHelpOpen(false);
                        setIsSupportDialogOpen(true);
                    }}
                />

                <AudienceCreationDialog
                    open={isAudienceDialogOpen}
                    handleClose={() => setIsAudienceDialogOpen(false)}
                    username={username}
                    password={password}
                    showSnackbar={showSnackbar}
                />

                <UsageDialog
                    email={username}
                    password={password}
                    open={isUsageDialogOpen}
                    onClose={() => setIsUsageDialogOpen(false)}
                    showSnackbar={showSnackbar}
                />

                <ResponsiveDialog open={isImprintDialogOpen} onClose={() => setIsImprintDialogOpen(false)} fullWidth maxWidth="sm">
                    <DialogTitle>{UI.DIALOGS.IMPRINT_TITLE}</DialogTitle>
                    <DialogContent dividers sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <Box>
                            <Typography variant="subtitle1" gutterBottom>{UI.IMPRINT.COMPANY}</Typography>
                            <Typography variant="body2">
                                {UI.IMPRINT.ADDRESS_LINE_1}<br />
                                {UI.IMPRINT.ADDRESS_LINE_2}<br />
                                {UI.IMPRINT.ADDRESS_LINE_3}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography variant="subtitle2" gutterBottom>{UI.IMPRINT.REPRESENTED_BY_TITLE}</Typography>
                            <Typography variant="body2">{UI.IMPRINT.REPRESENTED_BY_VALUE}</Typography>
                        </Box>
                        <Box>
                            <Typography variant="subtitle2" gutterBottom>{UI.IMPRINT.CONTACT_TITLE}</Typography>
                            <Typography variant="body2">{UI.IMPRINT.CONTACT_EMAIL}<br />{UI.IMPRINT.CONTACT_PHONE}</Typography>
                        </Box>
                        <Box>
                            <Typography variant="subtitle2" gutterBottom>{UI.IMPRINT.REGISTER_TITLE}</Typography>
                            <Typography variant="body2">{UI.IMPRINT.REGISTER_COURT}<br />{UI.IMPRINT.REGISTER_NUMBER}</Typography>
                        </Box>
                        <Box>
                            <Typography variant="subtitle2" gutterBottom>{UI.IMPRINT.VAT_TITLE}: {UI.IMPRINT.VAT_VALUE}</Typography>
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setIsImprintDialogOpen(false)}>{UI.BUTTONS.CLOSE}</Button>
                    </DialogActions>
                </ResponsiveDialog>

                <SupportContactDialog open={isSupportDialogOpen} onClose={() => setIsSupportDialogOpen(false)} />

                <OrderPackageDialog
                    open={isPackageDialogOpen}
                    onClose={() => setIsPackageDialogOpen(false)}
                    username={username}
                    password={password}
                    showSnackbar={showSnackbar}
                />

                <UserProfileDialog
                    open={isProfileDialogOpen}
                    onClose={() => setIsProfileDialogOpen(false)}
                    username={username}
                    password={password}
                    showSnackbar={showSnackbar}
                />

                <Snackbar
                    open={snackbarState.open}
                    autoHideDuration={3000}
                    onClose={closeSnackbar}
                    anchorOrigin={{ vertical: "top", horizontal: "center" }}
                    message={snackbarState.message}
                    ContentProps={{ sx: { maxWidth: { xs: "calc(100vw - 32px)", sm: 600 }, overflowWrap: "anywhere" } }}
                    sx={{ zIndex: 3001, top: { xs: 16, sm: 24 } }}
                />
            </Box>
        </ThemeProvider>
    );
}

export default Main;